import { useEffect, useState } from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { PageHeader } from "@/components/portal/PageHeader"
import { FilterBar } from "@/components/portal/FilterBar"
import { Loading, ErrorBox } from "@/components/portal/LoadingError"
import { toast } from "@/components/portal/Toast"
import { useTenantFilter } from "@/hooks/useTenantFilter"
import { SpielregelnCard } from "@/components/portal/SpielregelnCard"
import { BuchhaltungAdresseCard } from "@/components/portal/BuchhaltungAdresseCard"
import { BetriebsregelnCard } from "@/components/portal/BetriebsregelnCard"

type ToneConfig = {
  mode: "professionell" | "freundlich" | "locker"
  form: "sie" | "du"
  notes: string
}

type ForwardRule = {
  terms: string[]
  forward_to: string
  label: string
}

type SmartInboxThresholds = {
  enabled: boolean
  call_missed_hours: number
  call_no_contact_hours: number
  call_no_project_hours: number
  email_unanswered_hours: number
  urgent_keywords: string
}

type ContextConfig = {
  tenant_slug: string
  tenant_name: string
  tone_config: ToneConfig
  escalation_topics: string[]
  phrases_avoid: string[]
  phrases_preferred: string[]
  ai_rules: string[]
  leistungsspektrum_ja?: string[]
  leistungsspektrum_nein?: string[]
  vorgehen_regeln?: { kategorie: string; gewerk: string; text: string }[]
  keine_planung_absender: string[]
  /** Mailarten, fuer die der Planer keinen Plan macht — leer = Regel aus. */
  ohne_plan_mailarten?: string[]
  forward_rules: ForwardRule[]
  /** #91: leer = „an Buchhaltung weiterleiten“ wird nie vorgeschlagen. */
  buchhaltung_email?: string
  smart_inbox?: SmartInboxThresholds
  reply_templates?: Record<string, string>
}

// Use-Cases im Backend (services/draft_prompt.REPLY_TEMPLATE_SCENARIOS) —
// gleiche Reihenfolge + Labels.
const REPLY_TEMPLATE_SCENARIOS: { key: string; label: string; hint: string; placeholder: string }[] = [
  {
    key: "neukunde_termin",
    label: "Terminvorschlag (Neukunde)",
    hint: "Unbekannter Absender fragt nach einem Termin",
    placeholder: "Sehr geehrte/r {{anrede}},\n\nvielen Dank für Ihre Anfrage. Ich schlage Ihnen folgende Termine vor:\n\n{{slot1}}\n{{slot2}}\n\nBitte teilen Sie mir mit, welcher Termin Ihnen passt.\n\nFreundlichen Gruß",
  },
  {
    key: "bestandskunde_termin",
    label: "Terminvorschlag (Bestandskunde)",
    hint: "Bekannter Kunde fragt nach einem Termin",
    placeholder: "Hallo {{name}},\n\ngerne. Wie wäre es mit:\n\n{{slot1}}\n{{slot2}}\n\nMelden Sie sich kurz wenn einer passt.\n\nFreundlichen Gruß",
  },
  {
    key: "termin_bestaetigung",
    label: "Terminbestätigung",
    hint: "Kunde bestätigt einen vorgeschlagenen Termin",
    placeholder: "Sehr geehrte/r {{anrede}},\n\nvielen Dank für Ihre Bestätigung. Wir kommen wie vereinbart am {{slot1}}.\n\nBis dahin!\n\nFreundlichen Gruß",
  },
  {
    key: "termin_absage",
    label: "Terminabsage",
    hint: "Kunde sagt einen Termin ab oder verschiebt",
    placeholder: "Sehr geehrte/r {{anrede}},\n\ndanke für Ihre Nachricht — der Termin am {{slot1}} ist abgesagt. Falls Sie einen neuen Termin wünschen, melden Sie sich gerne.\n\nFreundlichen Gruß",
  },
  {
    key: "rueckfrage",
    label: "Rückfrage zur Anfrage",
    hint: "Du musst fehlende Infos vom Kunden nachfragen",
    placeholder: "Sehr geehrte/r {{anrede}},\n\nvielen Dank für Ihre Anfrage. Damit ich Ihnen weiterhelfen kann, brauche ich noch folgende Info:\n\n{{frage}}\n\nFreundlichen Gruß",
  },
  {
    key: "reply_only",
    label: "Allgemeine Antwort (Fallback)",
    hint: "Wird genutzt wenn keine der obigen Kategorien passt",
    placeholder: "Sehr geehrte/r {{anrede}},\n\n{{antwort}}\n\nFreundlichen Gruß",
  },
]

type PipelineThreshold = {
  step_id: number
  step_name: string
  threshold: number
  unit: "h" | "d"
  priority: "low" | "medium" | "high" | "critical"
  enabled: boolean
}

type PipelineTransitionRule = {
  trigger_pattern: string
  from_step_id: number | null
  to_step_id: number
  to_step_name: string
  enabled: boolean
}

type PipelineStep = { id: number; name: string; status_code?: number | null }

type BookingStepMap = {
  neukunde_step_id: number | null
  umsetzung_step_id: number | null
  umsetzung_trigger_step_ids: number[]
  immer_umsetzung?: boolean
}

type BookingStepMapSettings = {
  trigger_email: boolean
  trigger_sipgate: boolean
  trigger_meiti: boolean
  trigger_review: boolean
  trigger_hero_polling: boolean
}

type DocType = { id: number; name: string }

type DocStepRule = {
  document_type_id: number
  document_type_name: string
  target_step_id: number
  target_step_name: string
}

type DocStepMap = { rules: DocStepRule[] }

// Aufgabe nach Dokument (#95): Dokumenttyp → Aufgabe am Vorgang
type DocTaskRule = {
  document_type_id: number
  document_type_name: string
  task_title: string
  due_days: number | null
  // #97: fester Empfaenger (HERO-USER-ID), null = ohne Zustaendigen
  target_user_id?: number | null
  target_user_name?: string
}

type DocTaskMap = { rules: DocTaskRule[] }

const DOC_TASK_STANDARD_TITEL = "Rechnung erstellen – {kunde} – {dokument} vom {datum}"

type CalendarCategory = { id: number; name: string }

type ApptStepRule = {
  calendar_category_id: number
  calendar_category_name: string
  target_step_id: number
  target_step_name: string
}

type ApptStepMap = { rules: ApptStepRule[] }

type ProjectTypeMapRule = {
  trigger_pattern: string
  type_id: number
  type_name: string
}

type MeasureMapRule = {
  trigger_pattern: string
  measure_id: number
  measure_name: string
}

type DurationMapRule = {
  trigger_pattern: string
  duration_minutes: number
  label: string
}

type PipelineConfig = {
  tenant_slug: string
  project_types: { id: number; name?: string }[]
  pipeline_steps: PipelineStep[]
  pipeline_steps_by_type: Record<string, PipelineStep[]>
  document_types: DocType[]
  thresholds_by_type: Record<string, PipelineThreshold[]>
  transition_rules_by_type: Record<string, PipelineTransitionRule[]>
  booking_step_map_by_type: Record<string, BookingStepMap>
  booking_step_map_settings: BookingStepMapSettings
  document_step_map_by_type: Record<string, DocStepMap>
  document_task_map_by_type?: Record<string, DocTaskMap>
  appointment_step_map_by_type?: Record<string, ApptStepMap>
  projecttype_map: ProjectTypeMapRule[]
  measure_map?: MeasureMapRule[]
  duration_map?: DurationMapRule[]
  available_measures?: { id: number; name?: string }[]
  calendar_categories?: CalendarCategory[]
  /** #105: Archivierungs-Erinnerung (30 Tage „Angebot versendet") an/aus. Standard aus. */
  archiv_erinnerung_aktiv?: boolean
  zahlung_archiv_typen?: number[]
  umsetzung_kategorie_id?: number | null
}

async function fetchContext(tenant: string): Promise<ContextConfig> {
  const r = await fetch(`/api/v1/context?tenant=${encodeURIComponent(tenant)}`, { credentials: "include" })
  if (!r.ok) throw new Error(`HTTP ${r.status}`)
  return r.json()
}

async function saveContext(tenant: string, patch: Partial<ContextConfig>): Promise<ContextConfig> {
  const r = await fetch(`/api/v1/context?tenant=${encodeURIComponent(tenant)}`, {
    method: "PUT", credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  })
  if (!r.ok) throw new Error(`HTTP ${r.status}`)
  return r.json()
}

/** #91: Stichwort-Vorschläge aus echten Vorgängen — samt Messung, was sie bringen. */
type ProjectTypeVorschlag = {
  type_id: number
  type_name: string
  neue_woerter: string[]
  begruendung: string
}
type ProjectTypeVorschlaege = {
  moeglich: boolean
  grund?: string
  geprueft_an: number
  zugeordnet_vorher: number
  zugeordnet_nachher: number
  mit_typ_vergleichbar: number
  richtig_vorher: number
  richtig_nachher: number
  vorschlaege: ProjectTypeVorschlag[]
  mehrdeutig: { wort: string; typen: string[]; hinweis: string }[]
}

async function fetchProjectTypeVorschlaege(tenant: string): Promise<ProjectTypeVorschlaege> {
  const r = await fetch(
    `/api/v1/context/pipeline/projekttyp-vorschlaege?tenant=${encodeURIComponent(tenant)}`,
    { credentials: "include" },
  )
  if (!r.ok) throw new Error(`HTTP ${r.status}`)
  return r.json()
}

async function fetchPipeline(tenant: string): Promise<PipelineConfig> {
  const r = await fetch(`/api/v1/context/pipeline?tenant=${encodeURIComponent(tenant)}`, { credentials: "include" })
  if (!r.ok) throw new Error(`HTTP ${r.status}`)
  return r.json()
}

async function savePipeline(tenant: string, patch: { thresholds_by_type?: Record<string, PipelineThreshold[]>; transition_rules_by_type?: Record<string, PipelineTransitionRule[]>; booking_step_map_by_type?: Record<string, BookingStepMap>; booking_step_map_settings?: BookingStepMapSettings; document_step_map_by_type?: Record<string, DocStepMap>; document_task_map_by_type?: Record<string, DocTaskMap>; appointment_step_map_by_type?: Record<string, ApptStepMap>; projecttype_map?: ProjectTypeMapRule[]; measure_map?: MeasureMapRule[]; duration_map?: DurationMapRule[]; archiv_erinnerung_aktiv?: boolean; zahlung_archiv_typen?: number[]; umsetzung_kategorie_id?: number }): Promise<PipelineConfig> {
  const r = await fetch(`/api/v1/context/pipeline?tenant=${encodeURIComponent(tenant)}`, {
    method: "PUT", credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  })
  if (!r.ok) throw new Error(`HTTP ${r.status}`)
  return r.json()
}

type Tab = "kommunikation" | "spielregeln" | "betriebsregeln" | "pipeline"

/**
 * Kontext-Center — Input fuer bessere Claude-Drafts.
 * Ebene 1 (voll): Tonfall, Eskalation, Phrasen
 * Ebene 2 (read-only): Aktive Regeln aus smart_inbox_config
 * Ebene 3 (placeholder): Lern-Vorschlaege
 */
export default function ContextCenter() {
  const [tenant, setTenant] = useTenantFilter()
  const [tab, setTab] = useState<Tab>("kommunikation")
  const qc = useQueryClient()
  const ctx = useQuery({
    queryKey: ["context", tenant],
    queryFn: () => fetchContext(tenant),
    enabled: !!tenant,
  })
  const pipeline = useQuery({
    queryKey: ["pipeline", tenant],
    queryFn: () => fetchPipeline(tenant),
    enabled: !!tenant && tab === "pipeline",
  })
  // #86: Reiter „Betriebsregeln" nur mit Recht (Sperre im Backend). Solange die
  // Rechte laden, bleibt er verborgen — er ist kein Arbeitsbereich.
  const konto = useQuery<{ rechte?: string[] }>({
    queryKey: ["konto"],
    queryFn: async () => {
      const r = await fetch("/api/v1/konto", { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
    staleTime: 1000 * 60 * 10,
  })
  const darfBetriebsregeln = !!konto.data?.rechte?.includes("betriebsregeln")

  return (
    <>
      <PageHeader title="Kontext-Center" />
      <FilterBar tenant={tenant} onTenantChange={setTenant} />
      {!tenant ? (
        <div className="pp-card" style={{ padding: 48, textAlign: "center", color: "var(--text-muted)" }}>
          Bitte Tenant waehlen.
        </div>
      ) : (
        <>
          <div style={{ display: "flex", gap: 6, padding: "12px 0", borderBottom: "0.5px solid rgba(44,44,42,0.08)", marginBottom: 18 }}>
            {([
              { key: "kommunikation", label: "Kommunikation" },
              { key: "spielregeln", label: "Spielregeln" },
              ...(darfBetriebsregeln ? [{ key: "betriebsregeln", label: "Betriebsregeln" }] : []),
              { key: "pipeline", label: "Pipeline" },
            ] as { key: Tab; label: string }[]).map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                style={{
                  background: tab === t.key ? "#3C3489" : "transparent",
                  color: tab === t.key ? "#fff" : "#5F5E5A",
                  border: "none", padding: "6px 14px", borderRadius: 6,
                  fontSize: 13, fontWeight: tab === t.key ? 600 : 500, cursor: "pointer",
                }}
              >{t.label}</button>
            ))}
          </div>
        {tab === "kommunikation" && (
          ctx.isLoading ? <Loading label="Lade Kontext…" /> :
          ctx.isError ? <ErrorBox error={ctx.error} /> :
          ctx.data ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 18, maxWidth: 860 }}>
          <Intro name={ctx.data.tenant_name} />
          <ToneCard
            value={ctx.data.tone_config}
            onSave={async (v) => {
              await saveContext(tenant, { tone_config: v } as any)
              qc.invalidateQueries({ queryKey: ["context", tenant] })
              toast("✓ Tonfall gespeichert")
            }}
          />
          <ListCard
            title="Eskalations-Themen"
            hint="Bei diesen Stichworten baut Claude KEINEN Draft sondern gibt einen Platzhalter zurueck (z.B. 'Mahnung', 'Beschwerde', 'Unfall'). Default-Themen sind bereits hart verdrahtet, hier kommen zusaetzliche hinzu."
            placeholder="z.B. Streit, Abmahnung, Kulanzfall"
            value={ctx.data.escalation_topics}
            onSave={async (arr) => {
              await saveContext(tenant, { escalation_topics: arr } as any)
              qc.invalidateQueries({ queryKey: ["context", tenant] })
              toast("✓ Eskalations-Themen gespeichert")
            }}
          />
          <ListCard
            title="Zu vermeidende Formulierungen"
            hint="Claude wird angewiesen, diese Formulierungen NICHT zu benutzen."
            placeholder="z.B. 'leider müssen wir Ihnen mitteilen'"
            value={ctx.data.phrases_avoid}
            onSave={async (arr) => {
              await saveContext(tenant, { phrases_avoid: arr } as any)
              qc.invalidateQueries({ queryKey: ["context", tenant] })
              toast("✓ Zu vermeidende Phrasen gespeichert")
            }}
          />
          <ListCard
            title="Bevorzugte Formulierungen"
            hint="Claude nutzt diese Formulierungen wenn passend (z.B. Gruss, Standard-Saetze)."
            placeholder="z.B. 'Herzliche Grüße aus Karlsruhe'"
            value={ctx.data.phrases_preferred}
            onSave={async (arr) => {
              await saveContext(tenant, { phrases_preferred: arr } as any)
              qc.invalidateQueries({ queryKey: ["context", tenant] })
              toast("✓ Bevorzugte Phrasen gespeichert")
            }}
          />
          {/* Leistungsspektrum (#82): Grundlage der Ampel am Rand jeder Mail
              und Kontext fuer den Planer. Sobald die Historie drei
              vergleichbare Faelle kennt, gewinnt sie gegen die Liste. */}
          <ListCard
            title="Leistungsspektrum — bieten wir an"
            hint="Leistungen, die der Betrieb macht (z.B. 'Unterhaltsreinigung', 'Heizungswartung'). Speist die Ampel 'Leistungsspektrum' in der Mail-Ansicht und den Planer. Sobald zu einer Anfrageart drei Auftraege oder Termine vorliegen, entscheidet die Historie, nicht die Liste."
            placeholder="z.B. 'Glasreinigung'"
            value={ctx.data.leistungsspektrum_ja || []}
            onSave={async (arr) => {
              await saveContext(tenant, { leistungsspektrum_ja: arr } as any)
              qc.invalidateQueries({ queryKey: ["context", tenant] })
              toast("✓ Leistungsspektrum gespeichert")
            }}
          />
          <ListCard
            title="Leistungsspektrum — bieten wir NICHT an"
            hint="Anfragen dazu bekommen eine rote Ampel; der Planer schlaegt dafuer keinen Termin und kein Projekt vor, sondern eine Antwort mit dem Hinweis, dass der Betrieb das nicht anbietet."
            placeholder="z.B. 'Dachdeckerarbeiten'"
            value={ctx.data.leistungsspektrum_nein || []}
            onSave={async (arr) => {
              await saveContext(tenant, { leistungsspektrum_nein: arr } as any)
              qc.invalidateQueries({ queryKey: ["context", tenant] })
              toast("✓ Leistungsspektrum gespeichert")
            }}
          />
          <ListCard
            title="Aktive Regeln"
            hint="Freie Anweisungen die Claude bei JEDEM Draft beruecksichtigt. Jede Zeile = eine Regel. Beispiele: 'Immer Du-Form verwenden', 'Bei Angeboten Preis nie im Draft erwaehnen', 'Verpasste Anrufe sind immer wichtig'."
            placeholder="z.B. 'Bei Terminanfragen immer Mo/Di vorschlagen'"
            value={ctx.data.ai_rules}
            onSave={async (arr) => {
              await saveContext(tenant, { ai_rules: arr } as any)
              qc.invalidateQueries({ queryKey: ["context", tenant] })
              toast("✓ Regeln gespeichert")
            }}
          />
          <ListCard
            title="Nicht beplanen"
            hint="Absender oder Domains, deren Mails GAR NICHT an die KI gehen — sie bekommen sofort einen skip-Plan mit Begründung. Für Anfragen, die der Betrieb grundsätzlich nicht beantwortet (z.B. Lead-Portale). Eine Zeile je Eintrag: ganze Adresse (angebotsservice@11880.com) oder Domain (11880.com, gilt auch für Unterdomains)."
            placeholder="z.B. 11880.com"
            value={ctx.data.keine_planung_absender || []}
            onSave={async (arr) => {
              await saveContext(tenant, { keine_planung_absender: arr } as any)
              qc.invalidateQueries({ queryKey: ["context", tenant] })
              toast("✓ Nicht-beplanen-Liste gespeichert")
            }}
          />
          <ListCard
            title="Mailarten ohne Plan"
            hint="Für diese Post macht die KI keinen Plan, die Mail bekommt nur „Keine Aktion nötig“ mit Begründung. Vorbelegt sind Lieferanten-Angebote und Bewerbungen, weil solche Pläne fast nie ausgeführt wurden. Zeile entfernen = diese Mailart wird wieder beplant. Alle Zeilen entfernen = Regel aus. Für eine einzelne Mail reicht „Neu planen“ mit einem Hinweis."
            placeholder="z.B. Schulungs- und Veranstaltungseinladungen"
            value={ctx.data.ohne_plan_mailarten || []}
            onSave={async (arr) => {
              await saveContext(tenant, { ohne_plan_mailarten: arr } as any)
              qc.invalidateQueries({ queryKey: ["context", tenant] })
              toast("✓ Mailarten ohne Plan gespeichert")
            }}
          />
          <ForwardRulesCard
            rules={ctx.data.forward_rules}
            onSave={async (rules) => {
              await saveContext(tenant, { forward_rules: rules } as any)
              qc.invalidateQueries({ queryKey: ["context", tenant] })
              toast("✓ Weiterleitungen gespeichert")
            }}
          />
          <BuchhaltungAdresseCard
            value={ctx.data.buchhaltung_email || ""}
            onSave={async (adresse) => {
              await saveContext(tenant, { buchhaltung_email: adresse } as any)
              qc.invalidateQueries({ queryKey: ["context", tenant] })
              toast(adresse ? "✓ Buchhaltungsadresse gespeichert" : "✓ Buchhaltungsadresse entfernt")
            }}
          />
          <ReplyTemplatesCard
            templates={ctx.data.reply_templates || {}}
            onSave={async (templates) => {
              await saveContext(tenant, { reply_templates: templates } as any)
              qc.invalidateQueries({ queryKey: ["context", tenant] })
              toast("✓ Antwort-Vorlagen gespeichert")
            }}
          />
          <SmartInboxThresholdsCard
            value={ctx.data.smart_inbox || {
              enabled: true, call_missed_hours: 1, call_no_contact_hours: 2,
              call_no_project_hours: 4, email_unanswered_hours: 24, urgent_keywords: "",
            }}
            onSave={async (si) => {
              await saveContext(tenant, { smart_inbox: si } as any)
              qc.invalidateQueries({ queryKey: ["context", tenant] })
              toast("✓ Schwellenwerte gespeichert")
            }}
          />
          <RelatedConfigCard />
          <LearnStubCard />
        </div>
          ) : null
        )}
        {/* Spielregeln (#83) auf einem eigenen Reiter: die Tabelle wurde so
            lang, dass auf „Kommunikation" niemand mehr an die Karten darunter
            kam (Paul, 14.09.2026). */}
        {tab === "spielregeln" && (
          <div style={{ maxWidth: 1040 }}>
            <SpielregelnCard
              tenant={tenant}
              onSave={async (regeln) => {
                await saveContext(tenant, { vorgehen_regeln: regeln } as any)
                qc.invalidateQueries({ queryKey: ["context", tenant] })
              }}
            />
          </div>
        )}
        {tab === "betriebsregeln" && darfBetriebsregeln && (
          <div style={{ maxWidth: 1040 }}>
            <BetriebsregelnCard tenant={tenant} />
          </div>
        )}
        {tab === "pipeline" && (
          pipeline.isLoading ? <Loading label="Lade Pipeline…" /> :
          pipeline.isError ? <ErrorBox error={pipeline.error} /> :
          pipeline.data ? (
            <PipelinePanel
              tenant={tenant}
              config={pipeline.data}
              onSaveThresholds={async (thresholds_by_type) => {
                await savePipeline(tenant, { thresholds_by_type })
                qc.invalidateQueries({ queryKey: ["pipeline", tenant] })
                toast("✓ Schwellenwerte gespeichert")
              }}
              onSaveTransitions={async (rules_by_type) => {
                await savePipeline(tenant, { transition_rules_by_type: rules_by_type })
                qc.invalidateQueries({ queryKey: ["pipeline", tenant] })
                toast("✓ Status-Uebergangs-Regeln gespeichert")
              }}
              onSaveBookingMap={async (map) => {
                await savePipeline(tenant, { booking_step_map_by_type: map })
                qc.invalidateQueries({ queryKey: ["pipeline", tenant] })
                toast("✓ Buchungs-Status gespeichert")
              }}
              onSaveDocumentMap={async (map) => {
                await savePipeline(tenant, { document_step_map_by_type: map })
                qc.invalidateQueries({ queryKey: ["pipeline", tenant] })
                toast("✓ Dokument-Status gespeichert")
              }}
              onSaveDocumentTaskMap={async (map) => {
                await savePipeline(tenant, { document_task_map_by_type: map })
                qc.invalidateQueries({ queryKey: ["pipeline", tenant] })
                toast("✓ Aufgabe nach Dokument gespeichert")
              }}
              onSaveAppointmentMap={async (map) => {
                await savePipeline(tenant, { appointment_step_map_by_type: map })
                qc.invalidateQueries({ queryKey: ["pipeline", tenant] })
                toast("✓ Termin-Status gespeichert")
              }}
              onSaveBookingSettings={async (s) => {
                await savePipeline(tenant, { booking_step_map_settings: s })
                qc.invalidateQueries({ queryKey: ["pipeline", tenant] })
                toast("✓ Auto-Status-Quellen gespeichert")
              }}
              onSaveArchivErinnerung={async (aktiv) => {
                await savePipeline(tenant, { archiv_erinnerung_aktiv: aktiv })
                qc.invalidateQueries({ queryKey: ["pipeline", tenant] })
                toast(aktiv ? "✓ Archivierungs-Erinnerung eingeschaltet" : "✓ Archivierungs-Erinnerung ausgeschaltet")
              }}
              onSaveUmsetzungKategorie={async (id) => {
                await savePipeline(tenant, { umsetzung_kategorie_id: id ?? 0 })
                qc.invalidateQueries({ queryKey: ["pipeline", tenant] })
                toast("✓ Terminart-Vorauswahl gespeichert")
              }}
              onSaveZahlungArchiv={async (typen) => {
                await savePipeline(tenant, { zahlung_archiv_typen: typen })
                qc.invalidateQueries({ queryKey: ["pipeline", tenant] })
                toast("✓ Bezahlt → Archiviert gespeichert")
              }}
              onSaveProjectTypeMap={async (rules) => {
                await savePipeline(tenant, { projecttype_map: rules })
                qc.invalidateQueries({ queryKey: ["pipeline", tenant] })
                toast("✓ Projekttyp-Mapping gespeichert")
              }}
              onSaveMeasureMap={async (rules) => {
                await savePipeline(tenant, { measure_map: rules })
                qc.invalidateQueries({ queryKey: ["pipeline", tenant] })
                toast("✓ Gewerk-Mapping gespeichert")
              }}
              onSaveDurationMap={async (rules) => {
                await savePipeline(tenant, { duration_map: rules })
                qc.invalidateQueries({ queryKey: ["pipeline", tenant] })
                toast("✓ Termin-Dauer-Mapping gespeichert")
              }}
            />
          ) : null
        )}
        </>
      )}
    </>
  )
}

function Intro({ name }: { name: string }) {
  return (
    <div className="pp-card" style={{ padding: 16, background: "#EEEDFE" }}>
      <div style={{ fontSize: 13, color: "#3C3489" }}>
        Je mehr Claude ueber <strong>{name}</strong> weiss, desto passender die Drafts.
        Alles hier fliesst direkt in jeden Antwort-Entwurf ein.
      </div>
    </div>
  )
}

function ToneCard({ value, onSave }: { value: ToneConfig; onSave: (v: ToneConfig) => Promise<void> }) {
  const [mode, setMode] = useState(value.mode)
  const [form, setForm] = useState(value.form)
  const [notes, setNotes] = useState(value.notes)
  const [saving, setSaving] = useState(false)
  useEffect(() => { setMode(value.mode); setForm(value.form); setNotes(value.notes) }, [value])

  const dirty = mode !== value.mode || form !== value.form || notes !== value.notes

  return (
    <div className="pp-card" style={{ padding: 18 }}>
      <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 10 }}>Tonfall</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <div>
          <label className="pp-form-label">Grundton</label>
          <select value={mode} onChange={(e) => setMode(e.target.value as ToneConfig["mode"])}>
            <option value="professionell">Professionell</option>
            <option value="freundlich">Freundlich</option>
            <option value="locker">Locker</option>
          </select>
        </div>
        <div>
          <label className="pp-form-label">Anrede</label>
          <select value={form} onChange={(e) => setForm(e.target.value as ToneConfig["form"])}>
            <option value="sie">Siezen</option>
            <option value="du">Duzen</option>
          </select>
        </div>
      </div>
      <div style={{ marginTop: 12 }}>
        <label className="pp-form-label">Besonderheiten (Freitext)</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          placeholder="z.B. 'keine Superlative, kurze Saetze, direkte Sprache'"
          style={{ width: "100%", fontSize: 13, lineHeight: 1.5 }}
        />
      </div>
      <div style={{ marginTop: 12, display: "flex", justifyContent: "flex-end" }}>
        <button
          type="button"
          disabled={!dirty || saving}
          onClick={async () => {
            setSaving(true)
            try { await onSave({ mode, form, notes }) } finally { setSaving(false) }
          }}
          className="pp-btn pp-btn-primary pp-btn-sm"
        >
          {saving ? "Speichert…" : "Speichern"}
        </button>
      </div>
    </div>
  )
}

function ListCard({
  title, hint, placeholder, value, onSave,
}: {
  title: string
  hint: string
  placeholder: string
  value: string[]
  onSave: (v: string[]) => Promise<void>
}) {
  const [items, setItems] = useState<string[]>(value)
  const [draft, setDraft] = useState("")
  const [saving, setSaving] = useState(false)
  useEffect(() => { setItems(value) }, [value])
  const dirty = JSON.stringify(items) !== JSON.stringify(value)

  const add = () => {
    const v = draft.trim()
    if (v && !items.includes(v)) {
      setItems([...items, v])
      setDraft("")
    }
  }
  const remove = (i: number) => setItems(items.filter((_, idx) => idx !== i))

  return (
    <div className="pp-card" style={{ padding: 18 }}>
      <div style={{ fontSize: 14, fontWeight: 500 }}>{title}</div>
      <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4, marginBottom: 12 }}>{hint}</div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
        {items.length === 0 && (
          <span style={{ fontSize: 12, color: "var(--text-muted)", fontStyle: "italic" }}>
            Noch nichts gepflegt.
          </span>
        )}
        {items.map((it, i) => (
          <span
            key={i}
            style={{
              background: "#F5F4F0", border: "0.5px solid rgba(44,44,42,0.15)",
              borderRadius: 999, padding: "4px 10px", fontSize: 12,
              display: "inline-flex", alignItems: "center", gap: 6,
            }}
          >
            {it}
            <button
              type="button"
              onClick={() => remove(i)}
              style={{ background: "none", border: "none", cursor: "pointer", color: "#888", fontSize: 14, padding: 0, lineHeight: 1 }}
              aria-label="Entfernen"
            >×</button>
          </span>
        ))}
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add() } }}
          placeholder={placeholder}
          style={{ flex: 1, fontSize: 13 }}
        />
        <button type="button" onClick={add} className="pp-btn pp-btn-sm">+ Hinzufuegen</button>
      </div>

      <div style={{ marginTop: 12, display: "flex", justifyContent: "flex-end" }}>
        <button
          type="button"
          disabled={!dirty || saving}
          onClick={async () => {
            setSaving(true)
            try { await onSave(items) } finally { setSaving(false) }
          }}
          className="pp-btn pp-btn-primary pp-btn-sm"
        >
          {saving ? "Speichert…" : "Speichern"}
        </button>
      </div>
    </div>
  )
}

function ForwardRulesCard({
  rules, onSave,
}: {
  rules: ForwardRule[]
  onSave: (rules: ForwardRule[]) => Promise<void>
}) {
  const [items, setItems] = useState<ForwardRule[]>(rules)
  const [saving, setSaving] = useState(false)
  useEffect(() => { setItems(rules) }, [rules])
  const dirty = JSON.stringify(items) !== JSON.stringify(rules)

  const addRule = () => setItems([...items, { terms: [], forward_to: "", label: "" }])
  const removeRule = (i: number) => setItems(items.filter((_, idx) => idx !== i))
  const updateRule = (i: number, patch: Partial<ForwardRule>) => {
    setItems(items.map((r, idx) => idx === i ? { ...r, ...patch } : r))
  }

  return (
    <div className="pp-card" style={{ padding: 18 }}>
      <div style={{ fontSize: 14, fontWeight: 500 }}>Weiterleitungen (z. B. Buchhaltung, Steuerbüro)</div>
      <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4, marginBottom: 12 }}>
        Enthält eine eingehende Mail einen der Begriffe, erscheint bei der Mail ein
        Knopf zum Weiterleiten an diese Adresse, und das Feld „Leistungsspektrum“
        zeigt „Weiterleiten“ statt „Nicht einschätzbar“. Beispiel: Begriffe
        „Lohnabrechnung, Lohn“ → steuerbuero@… → „An Steuerbüro“.
      </div>

      {items.length === 0 && (
        <div style={{ fontSize: 12, color: "var(--text-muted)", fontStyle: "italic", marginBottom: 10 }}>
          Noch keine Weiterleitungen hinterlegt.
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {items.map((rule, i) => (
          <div key={i} style={{
            border: "0.5px solid rgba(44,44,42,0.15)", borderRadius: 8, padding: 12,
            display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: 8, alignItems: "flex-end",
          }}>
            <div>
              <label className="pp-form-label" style={{ fontSize: 12 }}>Begriffe (komma-getrennt)</label>
              <input
                value={rule.terms.join(", ")}
                onChange={(e) => updateRule(i, { terms: e.target.value.split(/[,;]/).map((t) => t.trim()).filter(Boolean) })}
                placeholder="Rechnung, Lastschrift, SEPA"
                style={{ fontSize: 13 }}
              />
            </div>
            <div>
              <label className="pp-form-label" style={{ fontSize: 12 }}>An E-Mail</label>
              <input
                type="email"
                value={rule.forward_to}
                onChange={(e) => updateRule(i, { forward_to: e.target.value })}
                placeholder="buchhaltung@firma.de"
                style={{ fontSize: 13 }}
              />
            </div>
            <div>
              <label className="pp-form-label" style={{ fontSize: 12 }}>Button-Text</label>
              <input
                value={rule.label}
                onChange={(e) => updateRule(i, { label: e.target.value })}
                placeholder="An Buchhaltung"
                style={{ fontSize: 13 }}
              />
            </div>
            <button
              type="button"
              onClick={() => removeRule(i)}
              style={{
                background: "transparent", border: "0.5px solid #D85A30", color: "#D85A30",
                borderRadius: 6, padding: "6px 10px", fontSize: 12, cursor: "pointer", height: 32,
              }}
            >Loeschen</button>
          </div>
        ))}
      </div>

      <div style={{ marginTop: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <button type="button" onClick={addRule} className="pp-btn pp-btn-sm">+ Regel hinzufuegen</button>
        <button
          type="button"
          disabled={!dirty || saving}
          onClick={async () => {
            setSaving(true)
            try { await onSave(items) } finally { setSaving(false) }
          }}
          className="pp-btn pp-btn-primary pp-btn-sm"
        >
          {saving ? "Speichert…" : "Speichern"}
        </button>
      </div>
    </div>
  )
}

function ReplyTemplatesCard({
  templates, onSave,
}: {
  templates: Record<string, string>
  onSave: (templates: Record<string, string>) => Promise<void>
}) {
  const [items, setItems] = useState<Record<string, string>>(templates)
  const [saving, setSaving] = useState(false)
  useEffect(() => { setItems(templates) }, [templates])
  const dirty = JSON.stringify(items) !== JSON.stringify(templates)

  return (
    <div className="pp-card" style={{ padding: 18 }}>
      <div style={{ fontSize: 14, fontWeight: 500 }}>Antwort-Vorlagen pro Use-Case</div>
      <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4, marginBottom: 14 }}>
        Pro Szenario ein Antwort-Skelett, an das Claude sich strikt halten soll. Bei einer
        kleinen Aenderung (z.B. anderer Termin) bleibt der Text stabil — nur die Platzhalter
        wie <code>{"{{slot1}}"}</code> werden ausgetauscht. Leer lassen = Claude generiert
        wie bisher freihaendig.
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {REPLY_TEMPLATE_SCENARIOS.map((sc) => (
          <div key={sc.key} style={{
            border: "0.5px solid rgba(44,44,42,0.12)", borderRadius: 8, padding: 12,
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
              <label className="pp-form-label" style={{ fontSize: 12, fontWeight: 600 }}>
                {sc.label}
              </label>
              <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{sc.hint}</span>
            </div>
            <textarea
              value={items[sc.key] || ""}
              onChange={(e) => setItems({ ...items, [sc.key]: e.target.value })}
              placeholder={sc.placeholder}
              rows={6}
              style={{ fontSize: 13, width: "100%", fontFamily: "ui-monospace, SFMono-Regular, monospace" }}
            />
          </div>
        ))}
      </div>

      <div style={{ marginTop: 12, display: "flex", justifyContent: "flex-end" }}>
        <button
          type="button"
          disabled={!dirty || saving}
          onClick={async () => {
            setSaving(true)
            try { await onSave(items) } finally { setSaving(false) }
          }}
          className="pp-btn pp-btn-primary pp-btn-sm"
        >
          {saving ? "Speichert…" : "Speichern"}
        </button>
      </div>
    </div>
  )
}

function SmartInboxThresholdsCard({
  value, onSave,
}: { value: SmartInboxThresholds; onSave: (v: SmartInboxThresholds) => Promise<void> }) {
  const [si, setSi] = useState<SmartInboxThresholds>(value)
  const [saving, setSaving] = useState(false)
  useEffect(() => { setSi(value) }, [value])
  const dirty = JSON.stringify(si) !== JSON.stringify(value)
  const upd = (patch: Partial<SmartInboxThresholds>) => setSi({ ...si, ...patch })
  const fieldStyle: React.CSSProperties = {
    width: "100%", padding: "6px 10px", border: "0.5px solid rgba(44,44,42,0.2)",
    borderRadius: 6, fontSize: 13, background: "#fff",
  }
  return (
    <div className="pp-card" style={{ padding: 18, display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ fontSize: 14, fontWeight: 500 }}>Smart Inbox — Dringlichkeits-Schwellenwerte</div>
      <div style={{ fontSize: 12, color: "#5F5E5A" }}>
        Nach wie vielen Stunden wird ein Item als dringend / eskaliert markiert.
      </div>
      <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
        <input
          type="checkbox"
          checked={si.enabled}
          onChange={(e) => upd({ enabled: e.target.checked })}
        />
        Smart Inbox aktiviert
      </label>
      {si.enabled && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Labeled label="Verpasste Anrufe nach (h)">
              <input type="number" min={0} value={si.call_missed_hours}
                onChange={(e) => upd({ call_missed_hours: parseInt(e.target.value) || 0 })}
                style={fieldStyle} />
            </Labeled>
            <Labeled label="Kein Kontakt zugeordnet (h)">
              <input type="number" min={0} value={si.call_no_contact_hours}
                onChange={(e) => upd({ call_no_contact_hours: parseInt(e.target.value) || 0 })}
                style={fieldStyle} />
            </Labeled>
            <Labeled label="Kein Projekt zugeordnet (h)">
              <input type="number" min={0} value={si.call_no_project_hours}
                onChange={(e) => upd({ call_no_project_hours: parseInt(e.target.value) || 0 })}
                style={fieldStyle} />
            </Labeled>
            <Labeled label="E-Mail unbeantwortet (h)">
              <input type="number" min={0} value={si.email_unanswered_hours}
                onChange={(e) => upd({ email_unanswered_hours: parseInt(e.target.value) || 0 })}
                style={fieldStyle} />
            </Labeled>
          </div>
          <Labeled label="Urgent-Keywords (komma-getrennt)" hint="E-Mails mit diesen Woertern werden sofort eskaliert.">
            <input
              type="text" value={si.urgent_keywords}
              onChange={(e) => upd({ urgent_keywords: e.target.value })}
              placeholder="dringend,notfall,wasserschaden,rohrbruch"
              style={fieldStyle}
            />
          </Labeled>
        </>
      )}
      {dirty && (
        <button
          type="button"
          disabled={saving}
          onClick={async () => { setSaving(true); try { await onSave(si) } finally { setSaving(false) } }}
          className="pp-btn pp-btn-sm pp-btn-primary"
          style={{ alignSelf: "flex-start" }}
        >{saving ? "Speichert…" : "Speichern"}</button>
      )}
    </div>
  )
}

function Labeled({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <div style={{ fontSize: 12, color: "#5F5E5A", fontWeight: 500 }}>{label}</div>
      {children}
      {hint && <div style={{ fontSize: 12, color: "#888780" }}>{hint}</div>}
    </div>
  )
}

function RelatedConfigCard() {
  return (
    <div className="pp-card" style={{ padding: 18, background: "#FAFAF8" }}>
      <div style={{ fontSize: 14, fontWeight: 500 }}>Verwandte Einstellungen</div>
      <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4, marginBottom: 12 }}>
        Folgende Regeln wirken ebenfalls auf Draft-/Terminvorschlaege — werden aber
        an anderer Stelle gepflegt (nicht duplizieren):
      </div>
      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.8, color: "var(--text-secondary)" }}>
        <li><strong>Signatur-HTML</strong>, <strong>Bueroszeiten</strong>, <strong>Booking-Config</strong> (Dauer/Lead-Time/Horizon),
            <strong> HERO Gewerke / Projekttypen</strong>, <strong>Pipeline-Regeln</strong> → im <a href="/tenants" style={{ color: "var(--accent-color)" }}>Tenant-Edit</a></li>
        <li><strong>Eskalations-Default-Keywords</strong> (Mahnung, Inkasso, Beschwerde, …) → hart im Code, hier nur Erweiterung</li>
      </ul>
    </div>
  )
}

function LearnStubCard() {
  return (
    <div className="pp-card" style={{
      padding: 18, background: "linear-gradient(135deg, #EEEDFE 0%, #E1F5EE 100%)",
    }}>
      <div style={{ fontSize: 14, fontWeight: 500 }}>Claude lernt mit</div>
      <div style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 6, lineHeight: 1.5 }}>
        Jede Draft-Korrektur von dir (edit+senden) wird als Few-Shot-Beispiel fuer
        zukuenftige Drafts gespeichert. Ab 3 aehnlichen Korrekturen erkennt Claude
        Muster und schlaegt automatisch neue Regeln vor — erscheinen hier in Phase C2b.
      </div>
    </div>
  )
}

function PipelineIntro() {
  return (
    <div className="pp-card" style={{ padding: 16, background: "#EEEDFE" }}>
      <div style={{ fontSize: 13, color: "#3C3489", lineHeight: 1.5 }}>
        <strong>Schwellenwerte</strong> bestimmen wann ein Projekt im Review-Reiter
        „Projekte" als „liegt zu lange" erscheint. <strong>Status-Uebergangs-Regeln</strong>
        helfen Claude beim Setzen des Pipeline-Steps wenn eine Mail eingeht
        (z.B. „Auftragsbestaetigung empfangen" → Step „Auftragsbestaetigung").
      </div>
    </div>
  )
}

function PipelinePanel({
  tenant, config, onSaveThresholds, onSaveTransitions, onSaveBookingMap, onSaveDocumentMap, onSaveDocumentTaskMap, onSaveAppointmentMap, onSaveBookingSettings, onSaveProjectTypeMap, onSaveMeasureMap, onSaveDurationMap, onSaveArchivErinnerung, onSaveZahlungArchiv, onSaveUmsetzungKategorie,
}: {
  tenant: string
  config: PipelineConfig
  onSaveThresholds: (v: Record<string, PipelineThreshold[]>) => Promise<void>
  onSaveTransitions: (v: Record<string, PipelineTransitionRule[]>) => Promise<void>
  onSaveBookingMap: (v: Record<string, BookingStepMap>) => Promise<void>
  onSaveDocumentMap: (v: Record<string, DocStepMap>) => Promise<void>
  onSaveDocumentTaskMap: (v: Record<string, DocTaskMap>) => Promise<void>
  onSaveAppointmentMap: (v: Record<string, ApptStepMap>) => Promise<void>
  onSaveBookingSettings: (v: BookingStepMapSettings) => Promise<void>
  onSaveProjectTypeMap: (v: ProjectTypeMapRule[]) => Promise<void>
  onSaveMeasureMap: (v: MeasureMapRule[]) => Promise<void>
  onSaveDurationMap: (v: DurationMapRule[]) => Promise<void>
  onSaveArchivErinnerung: (v: boolean) => Promise<void>
  onSaveZahlungArchiv: (typen: number[]) => Promise<void>
  onSaveUmsetzungKategorie: (id: number | null) => Promise<void>
}) {
  const types = config.project_types || []
  const [activeType, setActiveType] = useState<string>(
    types[0]?.id ? String(types[0].id) : "",
  )
  useEffect(() => {
    // Wenn aktiver Type nach Reload nicht mehr existiert, ersten waehlen
    if (types.length > 0 && !types.some((t) => String(t.id) === activeType)) {
      setActiveType(String(types[0].id))
    }
  }, [types, activeType])

  const stepsForType = config.pipeline_steps_by_type[activeType] || []

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18, maxWidth: 1000 }}>
      <PipelineIntro />
      {types.length > 0 && (
        <ProjectTypeMapCard
          rules={config.projecttype_map}
          projectTypes={types}
          onSave={onSaveProjectTypeMap}
          tenant={config.tenant_slug}
        />
      )}
      {(config.available_measures?.length || 0) > 0 && (
        <MeasureMapCard
          rules={config.measure_map || []}
          measures={config.available_measures || []}
          onSave={onSaveMeasureMap}
        />
      )}
      <DurationMapCard
        rules={config.duration_map || []}
        onSave={onSaveDurationMap}
      />
      {types.length === 0 ? (
        <div className="pp-card" style={{ padding: 24, textAlign: "center", color: "var(--text-muted)", fontSize: 13 }}>
          Keine Projekttypen aus HERO geladen. Bitte HERO-Token im Tenant pruefen.
        </div>
      ) : (
        <>
          {types.length > 1 && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {types.map((t) => {
                const k = String(t.id)
                const active = k === activeType
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setActiveType(k)}
                    style={{
                      background: active ? "#3C3489" : "#fff",
                      color: active ? "#fff" : "#5F5E5A",
                      border: active ? "0.5px solid #3C3489" : "0.5px solid rgba(44,44,42,0.15)",
                      padding: "6px 14px", borderRadius: 6,
                      fontSize: 13, fontWeight: active ? 600 : 500, cursor: "pointer",
                    }}
                  >{t.name || `Typ ${t.id}`}</button>
                )
              })}
            </div>
          )}
          <ThresholdsCard
            typeKey={activeType}
            steps={stepsForType}
            allByType={config.thresholds_by_type}
            onSave={onSaveThresholds}
          />
          <ArchivErinnerungCard
            value={!!config.archiv_erinnerung_aktiv}
            onSave={onSaveArchivErinnerung}
          />
          <ZahlungArchivCard
            typeKey={activeType}
            typeName={(config.project_types || []).find(t => String(t.id) === String(activeType))?.name || ""}
            typen={config.zahlung_archiv_typen || []}
            onSave={onSaveZahlungArchiv}
          />
          <BookingSettingsCard
            value={config.booking_step_map_settings}
            onSave={onSaveBookingSettings}
          />
          <BookingStepMapCard
            typeKey={activeType}
            steps={stepsForType}
            allByType={config.booking_step_map_by_type}
            onSave={onSaveBookingMap}
          />
          <UmsetzungKategorieCard
            value={config.umsetzung_kategorie_id ?? null}
            categories={config.calendar_categories || []}
            onSave={onSaveUmsetzungKategorie}
          />
          <DocumentStepMapCard
            typeKey={activeType}
            steps={stepsForType}
            documentTypes={config.document_types}
            allByType={config.document_step_map_by_type}
            onSave={onSaveDocumentMap}
          />
          <DocumentTaskMapCard
            tenant={tenant}
            typeKey={activeType}
            documentTypes={config.document_types}
            allByType={config.document_task_map_by_type || {}}
            onSave={onSaveDocumentTaskMap}
          />
          <AppointmentStepMapCard
            typeKey={activeType}
            steps={stepsForType}
            calendarCategories={config.calendar_categories || []}
            allByType={config.appointment_step_map_by_type || {}}
            onSave={onSaveAppointmentMap}
          />
          <TransitionRulesCard
            typeKey={activeType}
            steps={stepsForType}
            allByType={config.transition_rules_by_type}
            onSave={onSaveTransitions}
          />
        </>
      )}
    </div>
  )
}

function ThresholdsCard({
  typeKey, steps, allByType, onSave,
}: {
  typeKey: string
  steps: PipelineStep[]
  allByType: Record<string, PipelineThreshold[]>
  onSave: (thresholds: Record<string, PipelineThreshold[]>) => Promise<void>
}) {
  // Alle Steps des aktiven Typs werden automatisch gerendert. Pro Step:
  // entweder existierender Eintrag aus allByType oder Default-Stub
  // (3 Tage / high / nicht aktiv). Nur enabled=true erscheint im Review.
  const buildFullList = (base: Record<string, PipelineThreshold[]>): PipelineThreshold[] => {
    const existing = new Map<number, PipelineThreshold>()
    for (const t of (base[typeKey] || [])) existing.set(t.step_id, t)
    return steps.map((s) => existing.get(s.id) || ({
      step_id: s.id, step_name: s.name,
      threshold: 3, unit: "d", priority: "high", enabled: false,
    } as PipelineThreshold))
  }

  const [items, setItems] = useState<Record<string, PipelineThreshold[]>>(allByType || {})
  const [saving, setSaving] = useState(false)
  useEffect(() => { setItems(allByType || {}) }, [allByType])

  const list = buildFullList(items)
  const dirty = JSON.stringify(items[typeKey] || []) !== JSON.stringify(allByType[typeKey] || [])

  const update = (stepId: number, patch: Partial<PipelineThreshold>) => {
    const next = list.map((t) => t.step_id === stepId ? { ...t, ...patch } : t)
    setItems({ ...items, [typeKey]: next })
  }

  return (
    <div className="pp-card" style={{ padding: 18 }}>
      <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>Schwellenwerte pro Pipeline-Step</div>
      <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 14 }}>
        Wie lange darf ein Projekt in einem Step liegen, bevor es im
        Review-Reiter „Projekte" erscheint? Nur Steps mit Haken „aktiv"
        loesen Alerts aus.
      </div>
      {steps.length === 0 ? (
        <div style={{ fontSize: 12, color: "var(--text-muted)", fontStyle: "italic" }}>
          Keine Pipeline-Steps fuer diesen Projekttyp aus HERO geladen.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {list.map((t) => (
            <div key={t.step_id} style={{
              display: "grid",
              // Vorher feste Pixel: Beim Schrumpfen gab immer nur die
              // erste Spalte nach — die mit dem laengsten Text.
              gridTemplateColumns: "minmax(0, 1fr) minmax(56px, 80px) minmax(64px, 90px) minmax(72px, 110px) minmax(56px, 80px)",
              gap: 8, alignItems: "center",
              padding: 10,
              background: t.enabled ? "#FAFAF8" : "transparent",
              border: "0.5px solid rgba(44,44,42,0.1)", borderRadius: 6,
              opacity: t.enabled ? 1 : 0.65,
            }}>
              <div style={{ fontSize: 13, fontWeight: 500 }}>{t.step_name || `Step ${t.step_id}`}</div>
              <input
                type="number" min={1} value={t.threshold}
                onChange={(e) => update(t.step_id, { threshold: parseInt(e.target.value) || 1 })}
                style={{ fontSize: 13 }}
              />
              <select value={t.unit} onChange={(e) => update(t.step_id, { unit: e.target.value as "h" | "d" })}>
                <option value="h">Stunden</option>
                <option value="d">Tage</option>
              </select>
              <select value={t.priority} onChange={(e) => update(t.step_id, { priority: e.target.value as PipelineThreshold["priority"] })}>
                <option value="low">low</option>
                <option value="medium">medium</option>
                <option value="high">high</option>
                <option value="critical">critical</option>
              </select>
              <label style={{ fontSize: 12, display: "inline-flex", alignItems: "center", gap: 4 }}>
                <input
                  type="checkbox" checked={t.enabled}
                  onChange={(e) => update(t.step_id, { enabled: e.target.checked })}
                />
                aktiv
              </label>
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end" }}>
        <button
          type="button"
          disabled={!dirty || saving}
          onClick={async () => {
            setSaving(true)
            try {
              // Nur Eintraege speichern bei denen aktiv=true ODER der User
              // bereits etwas eingestellt hat (Wert vom Default abweicht).
              const toSave = list.filter((t) => t.enabled)
              await onSave({ ...items, [typeKey]: toSave })
            } finally { setSaving(false) }
          }}
          className="pp-btn pp-btn-primary pp-btn-sm"
        >{saving ? "Speichert…" : "Speichern"}</button>
      </div>
    </div>
  )
}

function ProjectTypeMapCard({
  rules, projectTypes, onSave, tenant,
}: {
  rules: ProjectTypeMapRule[]
  projectTypes: { id: number; name?: string }[]
  onSave: (v: ProjectTypeMapRule[]) => Promise<void>
  tenant: string
}) {
  const [items, setItems] = useState<ProjectTypeMapRule[]>(rules || [])
  const [saving, setSaving] = useState(false)
  useEffect(() => { setItems(rules || []) }, [rules])
  const dirty = JSON.stringify(items) !== JSON.stringify(rules || [])

  const addRule = () => {
    const first = projectTypes[0]
    if (!first) return
    setItems([...items, { trigger_pattern: "", type_id: first.id, type_name: first.name || `Typ ${first.id}` }])
  }
  const remove = (i: number) => setItems(items.filter((_, idx) => idx !== i))
  const update = (i: number, patch: Partial<ProjectTypeMapRule>) => {
    setItems(items.map((r, idx) => {
      if (idx !== i) return r
      const next = { ...r, ...patch }
      if (patch.type_id != null) {
        const pt = projectTypes.find((x) => x.id === patch.type_id)
        if (pt) next.type_name = pt.name || `Typ ${pt.id}`
      }
      return next
    }))
  }

  // #91: Vorschläge aus den echten Vorgängen. Bewusst auf Knopfdruck statt
  // automatisch — der Aufruf kostet, und niemand braucht ihn bei jedem Blick
  // auf die Seite.
  const [vorschlag, setVorschlag] = useState<ProjectTypeVorschlaege | null>(null)
  const [holt, setHolt] = useState(false)
  const [fehler, setFehler] = useState("")
  const holeVorschlaege = async () => {
    setHolt(true); setFehler("")
    try {
      setVorschlag(await fetchProjectTypeVorschlaege(tenant))
    } catch {
      setFehler("Die Vorschläge ließen sich gerade nicht laden.")
    } finally {
      setHolt(false)
    }
  }
  /** Übernimmt die Wörter eines Vorschlags in die Regel — gespeichert wird
   *  erst mit „Speichern", damit man sie vorher noch ändern kann. */
  const uebernehmen = (v: ProjectTypeVorschlag) => {
    setItems(items.map((r) => {
      if (r.type_id !== v.type_id) return r
      const vorhanden = (r.trigger_pattern || "").split(",").map((w) => w.trim()).filter(Boolean)
      const dazu = v.neue_woerter.filter((w) => !vorhanden.includes(w))
      return { ...r, trigger_pattern: [...vorhanden, ...dazu].join(",") }
    }))
    setVorschlag(vorschlag
      ? { ...vorschlag, vorschlaege: vorschlag.vorschlaege.filter((x) => x.type_id !== v.type_id) }
      : null)
  }

  return (
    <div className="pp-card" style={{ padding: 18 }}>
      <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>Projekttyp-Mapping (Mail-Stichwort → HERO-Type)</div>
      <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 14, lineHeight: 1.5 }}>
        Beim Anlegen eines Projekts im Review entscheidet der Agent welcher
        HERO-Type genutzt wird. Erstes matchendes Pattern gewinnt. Wenn keins
        matcht → Tenant-Default (im Tenant-Edit gesetzt).
      </div>

      {projectTypes.length === 0 ? (
        <div style={{ fontSize: 12, color: "var(--text-muted)", fontStyle: "italic" }}>
          Keine HERO Projekttypen geladen.
        </div>
      ) : (
        <>
          {items.length === 0 && (
            <div style={{ fontSize: 12, color: "var(--text-muted)", fontStyle: "italic", marginBottom: 10 }}>
              Keine Regeln. „+ Regel hinzufuegen" um die erste anzulegen
              (z.B. „wartung,inspektion,jährlich" → Kundendienst).
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {items.map((r, i) => (
              <div key={i} style={{
                display: "grid",
                gridTemplateColumns: "1.5fr auto 1fr auto",
                gap: 8, alignItems: "center",
                padding: 10, background: "#FAFAF8",
                border: "0.5px solid rgba(44,44,42,0.1)", borderRadius: 6,
              }}>
                <input
                  value={r.trigger_pattern}
                  onChange={(e) => update(i, { trigger_pattern: e.target.value })}
                  placeholder="wartung, inspektion, jaehrlich"
                  style={{ fontSize: 13 }}
                />
                <span style={{ fontSize: 13, color: "var(--text-muted)" }}>→</span>
                <select
                  value={r.type_id}
                  onChange={(e) => update(i, { type_id: parseInt(e.target.value) })}
                  style={{ fontSize: 13 }}
                >
                  {projectTypes.map((pt) => (
                    <option key={pt.id} value={pt.id}>{pt.name || `Typ ${pt.id}`}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => remove(i)}
                  style={{ background: "transparent", border: "0.5px solid #D85A30", color: "#D85A30", borderRadius: 6, padding: "4px 10px", fontSize: 12, cursor: "pointer" }}
                >×</button>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 12 }}>
            <button
              type="button"
              onClick={addRule}
              className="pp-btn pp-btn-sm"
              style={{ background: "transparent", border: "0.5px solid rgba(44,44,42,0.2)", padding: "4px 12px", fontSize: 12, borderRadius: 6, cursor: "pointer" }}
            >+ Regel hinzufuegen</button>
          </div>
        </>
      )}

      {/* #91: „KI macht Vorschläge welche Wörter, dann anpassbar im Portal" (Paul).
          Der Vorschlag wird gemessen, nicht geglaubt — daneben steht, wie viele
          echte Vorgänge vorher und nachher einen Typ bekämen. */}
      <div style={{ marginTop: 18, paddingTop: 14, borderTop: "0.5px solid rgba(44,44,42,0.12)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={holeVorschlaege}
            disabled={holt || projectTypes.length === 0}
            className="pp-btn pp-btn-sm"
            style={{ background: "transparent", border: "0.5px solid rgba(44,44,42,0.2)", padding: "4px 12px", fontSize: 12, borderRadius: 6, cursor: "pointer" }}
          >{holt ? "Sieht die Vorgänge durch…" : "Stichwörter vorschlagen"}</button>
          <span style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
            Liest die echten Vorgangsbezeichnungen dieses Betriebs und schlägt vor, was in der Liste fehlt.
          </span>
        </div>

        {fehler && <div style={{ fontSize: 12, color: "#D85A30", marginTop: 10 }}>{fehler}</div>}

        {vorschlag && !vorschlag.moeglich && (
          <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 10, fontStyle: "italic" }}>
            {vorschlag.grund}
          </div>
        )}

        {vorschlag?.moeglich && (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 12, marginBottom: 10, lineHeight: 1.6 }}>
              An <strong>{vorschlag.geprueft_an}</strong> echten Vorgängen gemessen:
              heute bekämen <strong>{vorschlag.zugeordnet_vorher}</strong> überhaupt
              einen Typ, mit den Vorschlägen <strong>{vorschlag.zugeordnet_nachher}</strong>.
              {vorschlag.mit_typ_vergleichbar > 0 && (
                <>
                  <br />
                  Und bei den <strong>{vorschlag.mit_typ_vergleichbar}</strong> Vorgängen, die
                  jemand von euch selbst einsortiert hat, träfe die Liste denselben Typ:
                  bisher <strong>{vorschlag.richtig_vorher}</strong>×,
                  danach <strong>{vorschlag.richtig_nachher}</strong>×.
                </>
              )}
            </div>

            {vorschlag.vorschlaege.length === 0 && vorschlag.mehrdeutig.length === 0 && (
              <div style={{ fontSize: 12, color: "var(--text-muted)", fontStyle: "italic" }}>
                Nichts zu ergänzen — die Liste deckt ab, was im Alltag vorkommt.
              </div>
            )}

            {vorschlag.vorschlaege.map((v) => (
              <div key={v.type_id} style={{ border: "0.5px solid rgba(44,44,42,0.15)", borderRadius: 8, padding: 12, marginBottom: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 6 }}>
                  <strong style={{ fontSize: 12.5 }}>{v.type_name}</strong>
                  <button
                    type="button"
                    onClick={() => uebernehmen(v)}
                    className="pp-btn pp-btn-sm"
                    style={{ background: "transparent", border: "0.5px solid rgba(44,44,42,0.2)", padding: "3px 10px", fontSize: 11.5, borderRadius: 6, cursor: "pointer" }}
                  >Übernehmen</button>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 6 }}>
                  {v.neue_woerter.map((w) => (
                    <span key={w} style={{ fontSize: 11, padding: "2px 7px", borderRadius: 4, background: "rgba(44,44,42,0.06)" }}>{w}</span>
                  ))}
                </div>
                {v.begruendung && (
                  <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>{v.begruendung}</div>
                )}
              </div>
            ))}

            {vorschlag.mehrdeutig.length > 0 && (
              <div style={{ marginTop: 4 }}>
                <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 4 }}>Das entscheidet ihr besser selbst</div>
                <div style={{ fontSize: 11.5, color: "var(--text-muted)", marginBottom: 6, lineHeight: 1.5 }}>
                  Diese Wörter tauchen bei mehreren Typen auf. Sie werden bewusst nicht
                  vorgeschlagen — sonst gewönne einfach der Typ, der weiter oben steht.
                </div>
                {vorschlag.mehrdeutig.map((m) => (
                  <div key={m.wort} style={{ fontSize: 11.5, marginBottom: 3 }}>
                    <span style={{ padding: "2px 7px", borderRadius: 4, background: "rgba(216,90,48,0.10)" }}>{m.wort}</span>
                    {" — "}{m.typen.join(" oder ")}{m.hinweis ? ` (${m.hinweis})` : ""}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div style={{ marginTop: 14, display: "flex", justifyContent: "flex-end" }}>
        <button
          type="button"
          disabled={!dirty || saving}
          onClick={async () => { setSaving(true); try { await onSave(items) } finally { setSaving(false) } }}
          className="pp-btn pp-btn-primary pp-btn-sm"
        >{saving ? "Speichert…" : "Speichern"}</button>
      </div>
    </div>
  )
}

function MeasureMapCard({
  rules, measures, onSave,
}: {
  rules: MeasureMapRule[]
  measures: { id: number; name?: string }[]
  onSave: (v: MeasureMapRule[]) => Promise<void>
}) {
  const [items, setItems] = useState<MeasureMapRule[]>(rules || [])
  const [saving, setSaving] = useState(false)
  useEffect(() => { setItems(rules || []) }, [rules])
  const dirty = JSON.stringify(items) !== JSON.stringify(rules || [])

  const addRule = () => {
    const first = measures[0]
    if (!first) return
    setItems([...items, { trigger_pattern: "", measure_id: first.id, measure_name: first.name || `Gewerk ${first.id}` }])
  }
  const remove = (i: number) => setItems(items.filter((_, idx) => idx !== i))
  const update = (i: number, patch: Partial<MeasureMapRule>) => {
    setItems(items.map((r, idx) => {
      if (idx !== i) return r
      const next = { ...r, ...patch }
      if (patch.measure_id != null) {
        const m = measures.find((x) => x.id === patch.measure_id)
        if (m) next.measure_name = m.name || `Gewerk ${m.id}`
      }
      return next
    }))
  }

  return (
    <div className="pp-card" style={{ padding: 18 }}>
      <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>Gewerk-Mapping (Mail-Stichwort → HERO-Gewerk)</div>
      <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 14, lineHeight: 1.5 }}>
        Beim Anlegen eines Projekts im Review entscheidet der Agent welches
        Gewerk (measure_id) zum Projekt gehoert. Erstes matchendes Pattern gewinnt.
        Wenn keins matcht → Claude waehlt aus den verfuegbaren Gewerken selbst.
      </div>

      {items.length === 0 && (
        <div style={{ fontSize: 12, color: "var(--text-muted)", fontStyle: "italic", marginBottom: 10 }}>
          Keine Regeln. „+ Regel hinzufuegen" um die erste anzulegen
          (z.B. „heizung,therme,heizkessel" → Heizung).
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {items.map((r, i) => (
          <div key={i} style={{
            display: "grid",
            gridTemplateColumns: "1.5fr auto 1fr auto",
            gap: 8, alignItems: "center",
            padding: 10, background: "#FAFAF8",
            border: "0.5px solid rgba(44,44,42,0.1)", borderRadius: 6,
          }}>
            <input
              value={r.trigger_pattern}
              onChange={(e) => update(i, { trigger_pattern: e.target.value })}
              placeholder="heizung, therme, heizkessel"
              style={{ fontSize: 13 }}
            />
            <span style={{ fontSize: 13, color: "var(--text-muted)" }}>→</span>
            <select
              value={r.measure_id}
              onChange={(e) => update(i, { measure_id: parseInt(e.target.value) })}
              style={{ fontSize: 13 }}
            >
              {measures.map((m) => (
                <option key={m.id} value={m.id}>{m.name || `Gewerk ${m.id}`}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => remove(i)}
              style={{ background: "transparent", border: "0.5px solid #D85A30", color: "#D85A30", borderRadius: 6, padding: "4px 10px", fontSize: 12, cursor: "pointer" }}
            >×</button>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 12 }}>
        <button
          type="button"
          onClick={addRule}
          className="pp-btn pp-btn-sm"
          style={{ background: "transparent", border: "0.5px solid rgba(44,44,42,0.2)", padding: "4px 12px", fontSize: 12, borderRadius: 6, cursor: "pointer" }}
        >+ Regel hinzufuegen</button>
      </div>

      <div style={{ marginTop: 14, display: "flex", justifyContent: "flex-end" }}>
        <button
          type="button"
          disabled={!dirty || saving}
          onClick={async () => { setSaving(true); try { await onSave(items) } finally { setSaving(false) } }}
          className="pp-btn pp-btn-primary pp-btn-sm"
        >{saving ? "Speichert…" : "Speichern"}</button>
      </div>
    </div>
  )
}


function DurationMapCard({
  rules, onSave,
}: {
  rules: DurationMapRule[]
  onSave: (v: DurationMapRule[]) => Promise<void>
}) {
  const [items, setItems] = useState<DurationMapRule[]>(rules || [])
  const [saving, setSaving] = useState(false)
  useEffect(() => { setItems(rules || []) }, [rules])
  const dirty = JSON.stringify(items) !== JSON.stringify(rules || [])

  const addRule = () => {
    setItems([...items, { trigger_pattern: "", duration_minutes: 90, label: "" }])
  }
  const remove = (i: number) => setItems(items.filter((_, idx) => idx !== i))
  const update = (i: number, patch: Partial<DurationMapRule>) => {
    setItems(items.map((r, idx) => idx === i ? { ...r, ...patch } : r))
  }

  return (
    <div className="pp-card" style={{ padding: 18 }}>
      <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>Termin-Dauer pro Typ</div>
      <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 14, lineHeight: 1.5 }}>
        Wie lange dauert ein Termin typischerweise? Erstes matchendes Pattern bestimmt
        slot.duration_minutes. Wenn keins matcht → Default vom Tenant (booking_config) greift.
        Gilt fuer alle Termin-Vorschlaege im Review.
      </div>

      {items.length === 0 && (
        <div style={{ fontSize: 12, color: "var(--text-muted)", fontStyle: "italic", marginBottom: 10 }}>
          Keine Regeln. „+ Regel hinzufuegen" um die erste anzulegen
          (z.B. „kundendienst,stoerung,defekt" → 120 Min).
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {items.map((r, i) => (
          <div key={i} style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1.5fr) auto minmax(64px, 90px) auto minmax(0, 1fr) auto",
            gap: 8, alignItems: "center",
            padding: 10, background: "#FAFAF8",
            border: "0.5px solid rgba(44,44,42,0.1)", borderRadius: 6,
          }}>
            <input
              value={r.trigger_pattern}
              onChange={(e) => update(i, { trigger_pattern: e.target.value })}
              placeholder="kundendienst, stoerung, defekt, undicht"
              style={{ fontSize: 13 }}
            />
            <span style={{ fontSize: 13, color: "var(--text-muted)" }}>→</span>
            <input
              type="number"
              min={15}
              max={480}
              step={15}
              value={r.duration_minutes}
              onChange={(e) => update(i, { duration_minutes: parseInt(e.target.value) || 0 })}
              style={{ fontSize: 13, textAlign: "right" }}
            />
            <span style={{ fontSize: 12, color: "var(--text-muted)" }}>Min</span>
            <input
              value={r.label}
              onChange={(e) => update(i, { label: e.target.value })}
              placeholder="Label (z.B. Kundendiensteinsatz)"
              style={{ fontSize: 13 }}
            />
            <button
              type="button"
              onClick={() => remove(i)}
              style={{ background: "transparent", border: "0.5px solid #D85A30", color: "#D85A30", borderRadius: 6, padding: "4px 10px", fontSize: 12, cursor: "pointer" }}
            >×</button>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 12 }}>
        <button
          type="button"
          onClick={addRule}
          className="pp-btn pp-btn-sm"
          style={{ background: "transparent", border: "0.5px solid rgba(44,44,42,0.2)", padding: "4px 12px", fontSize: 12, borderRadius: 6, cursor: "pointer" }}
        >+ Regel hinzufuegen</button>
      </div>

      <div style={{ marginTop: 14, display: "flex", justifyContent: "flex-end" }}>
        <button
          type="button"
          disabled={!dirty || saving}
          onClick={async () => { setSaving(true); try { await onSave(items) } finally { setSaving(false) } }}
          className="pp-btn pp-btn-primary pp-btn-sm"
        >{saving ? "Speichert…" : "Speichern"}</button>
      </div>
    </div>
  )
}


/** #105 (Marlene, 16.09.2026): „Bitte das Erstellen von Aufgaben zu Schwelle bei
 *  Projektstatus stoppen!" — die Erinnerung aus #5 (30 Tage „Angebot versendet"
 *  → Aufgabe „Archivieren?" fuers Buero) legte bei Sanitec in zwei Wochen 15
 *  Aufgaben an, die niemand wollte. Seitdem ist sie je Betrieb ein Schalter,
 *  Standard aus. Gilt fuer alle Projekttypen des Betriebs. */
/** Rechnung bezahlt → „Archiviert" (Armans 1.3). Je Projekttyp ein Haken —
 *  so bleibt z. B. Wartung aussen vor. Als bezahlt zaehlt nur eine Rechnung
 *  mit Zahldatum in HERO; Storno-Paare stehen dort auch auf „Bezahlt". */
export function ZahlungArchivCard({
  typeKey, typeName, typen, onSave,
}: {
  typeKey: string
  typeName: string
  typen: number[]
  onSave: (typen: number[]) => Promise<void>
}) {
  const typId = Number(typeKey)
  const gespeichert = typen.includes(typId)
  const [aktiv, setAktiv] = useState<boolean>(gespeichert)
  const [saving, setSaving] = useState(false)
  useEffect(() => { setAktiv(gespeichert) }, [gespeichert, typeKey])
  if (!typeKey || Number.isNaN(typId)) return null
  const dirty = aktiv !== gespeichert

  return (
    <div className="pp-card" style={{ padding: 18 }}>
      <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>Bezahlt → Archiviert</div>
      <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 14, lineHeight: 1.5 }}>
        Steht ein Vorgang auf dem Zahlungs-Schritt und sind alle Rechnungen in HERO bezahlt
        (mit Datum „Bezahlt am“ — Storno-Paare zaehlen nicht), geht er automatisch auf
        „Archiviert“. Einmal am Tag wird jeder so archivierte Vorgang erneut geprueft; fehlt
        die Zahlung inzwischen, kommt er zurueck und es gibt einen Alarm.
      </div>
      <label style={{
        display: "grid", gridTemplateColumns: "auto 1fr", gap: 10,
        padding: 10, background: aktiv ? "#FAFAF8" : "transparent",
        border: "0.5px solid rgba(44,44,42,0.1)", borderRadius: 6,
        cursor: "pointer",
      }}>
        <input
          type="checkbox"
          checked={aktiv}
          onChange={(e) => setAktiv(e.target.checked)}
          aria-label="Bezahlt → Archiviert fuer diesen Projekttyp"
        />
        <div>
          <div style={{ fontSize: 13, fontWeight: 500 }}>Fuer {typeName || `Typ ${typeKey}`} einschalten</div>
          <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
            {aktiv ? "Eingeschaltet — laeuft alle 15 Minuten." : "Ausgeschaltet — bezahlte Vorgaenge bleiben stehen."}
          </div>
        </div>
      </label>
      <div style={{ marginTop: 14, display: "flex", justifyContent: "flex-end" }}>
        <button
          type="button"
          disabled={!dirty || saving}
          onClick={async () => {
            setSaving(true)
            try {
              const rest = typen.filter(t => t !== typId)
              await onSave(aktiv ? [...rest, typId] : rest)
            } finally { setSaving(false) }
          }}
          className="pp-btn pp-btn-primary pp-btn-sm"
        >{saving ? "Speichert…" : "Speichern"}</button>
      </div>
    </div>
  )
}

export function ArchivErinnerungCard({
  value, onSave,
}: {
  value: boolean
  onSave: (v: boolean) => Promise<void>
}) {
  const [aktiv, setAktiv] = useState<boolean>(value)
  const [saving, setSaving] = useState(false)
  useEffect(() => { setAktiv(value) }, [value])
  const dirty = aktiv !== value

  return (
    <div className="pp-card" style={{ padding: 18 }}>
      <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>Archivierungs-Erinnerung</div>
      <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 14, lineHeight: 1.5 }}>
        Steht ein Vorgang 30 Tage auf „Angebot versendet" oder „Angebot nachgehakt",
        bekommt das Buero einmal je Vorgang eine Aufgabe „Archivieren?". Gilt fuer
        alle Projekttypen dieses Betriebs. Standard: aus — bereits angelegte
        Aufgaben bleiben beim Ausschalten bestehen.
      </div>
      <label style={{
        display: "grid", gridTemplateColumns: "auto 1fr", gap: 10,
        padding: 10, background: aktiv ? "#FAFAF8" : "transparent",
        border: "0.5px solid rgba(44,44,42,0.1)", borderRadius: 6,
        cursor: "pointer",
      }}>
        <input
          type="checkbox"
          checked={aktiv}
          onChange={(e) => setAktiv(e.target.checked)}
          aria-label="Archivierungs-Erinnerung aktiv"
        />
        <div>
          <div style={{ fontSize: 13, fontWeight: 500 }}>Aufgabe fuers Buero anlegen</div>
          <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
            {aktiv ? "Eingeschaltet — der Lauf prueft einmal taeglich." : "Ausgeschaltet — es entstehen keine Aufgaben aus der Status-Schwelle."}
          </div>
        </div>
      </label>
      <div style={{ marginTop: 14, display: "flex", justifyContent: "flex-end" }}>
        <button
          type="button"
          disabled={!dirty || saving}
          onClick={async () => { setSaving(true); try { await onSave(aktiv) } finally { setSaving(false) } }}
          className="pp-btn pp-btn-primary pp-btn-sm"
        >{saving ? "Speichert…" : "Speichern"}</button>
      </div>
    </div>
  )
}

function BookingSettingsCard({
  value, onSave,
}: {
  value: BookingStepMapSettings
  onSave: (v: BookingStepMapSettings) => Promise<void>
}) {
  const [items, setItems] = useState<BookingStepMapSettings>(value)
  const [saving, setSaving] = useState(false)
  useEffect(() => { setItems(value) }, [value])
  const dirty = JSON.stringify(items) !== JSON.stringify(value)

  const sources: { key: keyof BookingStepMapSettings; label: string; hint: string }[] = [
    { key: "trigger_email", label: "E-Mail-Buchungen", hint: "BookingModal in /emails" },
    { key: "trigger_review", label: "Review-Agent", hint: "Auto-Buchung im Review (nur wenn Plan keinen Step setzt)" },
    { key: "trigger_sipgate", label: "Sipgate-Buchungen", hint: "BookingModal in /sipgate" },
    { key: "trigger_meiti", label: "Meiti-Buchungen", hint: "BookingModal in /calls (Meiti)" },
    { key: "trigger_hero_polling", label: "Direkte Kalender-Termine (HERO + Portal-Kalender)", hint: "Pollt alle 15 Min den Kalender-Cache — greift fuer Termine die direkt in HERO oder in der Portal-Kalender-Ansicht erstellt wurden (also alles ausser E-Mail/Sipgate/Meiti/Review)" },
  ]

  return (
    <div className="pp-card" style={{ padding: 18 }}>
      <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>Auto-Status nach Termin: aktive Quellen</div>
      <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 14, lineHeight: 1.5 }}>
        Welche Buchungs-Quellen sollen die unten konfigurierte Status-Logik
        ausloesen? Defaults: E-Mail/Sipgate/Meiti aktiv, Review + HERO-Polling opt-in.
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {sources.map((s) => (
          <label key={s.key} style={{
            display: "grid", gridTemplateColumns: "auto 1fr", gap: 10,
            padding: 10, background: items[s.key] ? "#FAFAF8" : "transparent",
            border: "0.5px solid rgba(44,44,42,0.1)", borderRadius: 6,
            cursor: "pointer",
          }}>
            <input
              type="checkbox"
              checked={items[s.key]}
              onChange={(e) => setItems({ ...items, [s.key]: e.target.checked })}
            />
            <div>
              <div style={{ fontSize: 13, fontWeight: 500 }}>{s.label}</div>
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>{s.hint}</div>
            </div>
          </label>
        ))}
      </div>
      <div style={{ marginTop: 14, display: "flex", justifyContent: "flex-end" }}>
        <button
          type="button"
          disabled={!dirty || saving}
          onClick={async () => { setSaving(true); try { await onSave(items) } finally { setSaving(false) } }}
          className="pp-btn pp-btn-primary pp-btn-sm"
        >{saving ? "Speichert…" : "Speichern"}</button>
      </div>
    </div>
  )
}

function BookingStepMapCard({
  typeKey, steps, allByType, onSave,
}: {
  typeKey: string
  steps: PipelineStep[]
  allByType: Record<string, BookingStepMap>
  onSave: (v: Record<string, BookingStepMap>) => Promise<void>
}) {
  const empty: BookingStepMap = { neukunde_step_id: null, umsetzung_step_id: null, umsetzung_trigger_step_ids: [] }
  const [items, setItems] = useState<Record<string, BookingStepMap>>(allByType || {})
  const [saving, setSaving] = useState(false)
  useEffect(() => { setItems(allByType || {}) }, [allByType])
  const dirty = JSON.stringify(items) !== JSON.stringify(allByType || {})

  const current = items[typeKey] || empty
  const update = (patch: Partial<BookingStepMap>) => {
    setItems({ ...items, [typeKey]: { ...current, ...patch } })
  }
  const toggleTrigger = (id: number) => {
    const has = current.umsetzung_trigger_step_ids.includes(id)
    update({
      umsetzung_trigger_step_ids: has
        ? current.umsetzung_trigger_step_ids.filter((x) => x !== id)
        : [...current.umsetzung_trigger_step_ids, id],
    })
  }

  return (
    <div className="pp-card" style={{ padding: 18 }}>
      <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>Status nach Termin-Buchung</div>
      <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 14, lineHeight: 1.5 }}>
        Wenn aus dem E-Mail-Reiter ein Termin gebucht wird, setzt das Portal den
        Projekt-Status automatisch auf einen dieser Steps. Leer lassen = kein Auto-Switch.
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
        <Labeled label="Status fuer Erstgespraech / Vor-Ort" hint="Default-Step bei normalen Termin-Buchungen.">
          <select
            value={current.neukunde_step_id ?? ""}
            onChange={(e) => update({ neukunde_step_id: e.target.value ? parseInt(e.target.value) : null })}
            style={{ fontSize: 13, width: "100%" }}
          >
            <option value="">— kein Auto-Switch —</option>
            {steps.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </Labeled>
        <Labeled label="Status fuer Umsetzungstermin" hint="Wird gesetzt wenn das Projekt vorher in einem der unten markierten Steps war.">
          <select
            value={current.umsetzung_step_id ?? ""}
            onChange={(e) => update({ umsetzung_step_id: e.target.value ? parseInt(e.target.value) : null })}
            style={{ fontSize: 13, width: "100%" }}
          >
            <option value="">— kein Auto-Switch —</option>
            {steps.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </Labeled>
      </div>

      <div style={{ marginBottom: 8, fontSize: 12, color: "var(--text-secondary)" }}>
        <strong>Aktuelle Status</strong>, die einen Umsetzungstermin ausloesen (Mehrfachauswahl):
      </div>
      {steps.length === 0 ? (
        <div style={{ fontSize: 12, color: "var(--text-muted)", fontStyle: "italic" }}>
          Keine Steps fuer diesen Projekttyp geladen.
        </div>
      ) : (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {steps.map((s) => {
            const checked = current.umsetzung_trigger_step_ids.includes(s.id)
            return (
              <label
                key={s.id}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  padding: "4px 10px", borderRadius: 6, fontSize: 12,
                  background: checked ? "#EEEDFE" : "#FAFAF8",
                  border: checked ? "0.5px solid #3C3489" : "0.5px solid rgba(44,44,42,0.1)",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleTrigger(s.id)}
                />
                {s.name}
              </label>
            )
          })}
        </div>
      )}

      <label style={{
        display: "grid", gridTemplateColumns: "auto 1fr", gap: 10, marginTop: 14,
        padding: 10, border: "0.5px solid rgba(44,44,42,0.1)", borderRadius: 6, cursor: "pointer",
      }}>
        <input
          type="checkbox"
          checked={!!current.immer_umsetzung}
          onChange={(e) => update({ immer_umsetzung: e.target.checked })}
          aria-label="Jeder Termin ist ein Umsetzungstermin"
        />
        <div>
          <div style={{ fontSize: 13, fontWeight: 500 }}>Jeder Termin ist ein Umsetzungstermin</div>
          <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
            Z. B. Wartung — dort gibt es keine Besichtigung. Steuert nur die vorausgewaehlte Terminart, nicht den Status.
          </div>
        </div>
      </label>

      <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end" }}>
        <button
          type="button"
          disabled={!dirty || saving}
          onClick={async () => { setSaving(true); try { await onSave(items) } finally { setSaving(false) } }}
          className="pp-btn pp-btn-primary pp-btn-sm"
        >{saving ? "Speichert…" : "Speichern"}</button>
      </div>
    </div>
  )
}

/** Terminart-Vorauswahl (Befund 23.10.2026: 41 % der „Vor-Ort-Termine" waren
 *  Umsetzungen). Mit gesetzter Kategorie bekommt ein Termin sie bei Typen mit
 *  „Jeder Termin ist ein Umsetzungstermin" und ab den Umsetzungs-Ausloeser-
 *  Schritten — in allen Dialogen, beim Telefonbot und per WhatsApp. */
export function UmsetzungKategorieCard({
  value, categories, onSave,
}: {
  value: number | null
  categories: { id: number; name: string }[]
  onSave: (id: number | null) => Promise<void>
}) {
  const [wahl, setWahl] = useState<number | null>(value)
  const [saving, setSaving] = useState(false)
  useEffect(() => { setWahl(value) }, [value])
  const dirty = wahl !== value
  return (
    <div className="pp-card" style={{ padding: 18 }}>
      <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>Terminart fuer Umsetzungstermine</div>
      <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 14, lineHeight: 1.5 }}>
        Diese Terminart wird vorausgewaehlt (und vom Telefonbot gebucht), wenn der Vorgang auf einem
        Schritt steht, der einen Umsetzungstermin ausloest, oder sein Projekttyp „Jeder Termin ist ein
        Umsetzungstermin" traegt. Sonst gilt die Kategorie des Betriebs. Leer = aus.
      </div>
      <select
        value={wahl ?? ""}
        onChange={(e) => setWahl(e.target.value ? parseInt(e.target.value) : null)}
        aria-label="Terminart fuer Umsetzungstermine"
        style={{ fontSize: 13, width: "100%" }}
      >
        <option value="">— aus (immer Betriebs-Kategorie) —</option>
        {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
      <div style={{ marginTop: 14, display: "flex", justifyContent: "flex-end" }}>
        <button
          type="button"
          disabled={!dirty || saving}
          onClick={async () => { setSaving(true); try { await onSave(wahl) } finally { setSaving(false) } }}
          className="pp-btn pp-btn-primary pp-btn-sm"
        >{saving ? "Speichert…" : "Speichern"}</button>
      </div>
    </div>
  )
}

function DocumentStepMapCard({
  typeKey, steps, documentTypes, allByType, onSave,
}: {
  typeKey: string
  steps: PipelineStep[]
  documentTypes: DocType[]
  allByType: Record<string, DocStepMap>
  onSave: (v: Record<string, DocStepMap>) => Promise<void>
}) {
  const empty: DocStepMap = { rules: [] }
  const [items, setItems] = useState<Record<string, DocStepMap>>(allByType || {})
  const [saving, setSaving] = useState(false)
  useEffect(() => { setItems(allByType || {}) }, [allByType])
  const dirty = JSON.stringify(items[typeKey] || empty) !== JSON.stringify(allByType[typeKey] || empty)

  const current = items[typeKey] || empty
  const setRules = (rules: DocStepRule[]) => {
    setItems({ ...items, [typeKey]: { rules } })
  }
  const addRule = () => {
    const dt = documentTypes[0]
    const st = steps[0]
    if (!dt || !st) return
    setRules([...current.rules, {
      document_type_id: dt.id, document_type_name: dt.name,
      target_step_id: st.id, target_step_name: st.name,
    }])
  }
  const remove = (i: number) => setRules(current.rules.filter((_, idx) => idx !== i))
  const update = (i: number, patch: Partial<DocStepRule>) => {
    setRules(current.rules.map((r, idx) => {
      if (idx !== i) return r
      const next = { ...r, ...patch }
      if (patch.document_type_id != null) {
        const d = documentTypes.find((x) => x.id === patch.document_type_id)
        if (d) next.document_type_name = d.name
      }
      if (patch.target_step_id != null) {
        const s = steps.find((x) => x.id === patch.target_step_id)
        if (s) next.target_step_name = s.name
      }
      return next
    }))
  }

  return (
    <div className="pp-card" style={{ padding: 18 }}>
      <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>Status nach Dokument-Erstellung</div>
      <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 14, lineHeight: 1.5 }}>
        Wenn in HERO ein Dokument eines bestimmten Typs erstellt wird, setzt das
        Portal automatisch den Projekt-Status. Wird alle 15 Min gepollt
        (Idempotenz: jedes Dokument wechselt den Status genau einmal).
      </div>

      {documentTypes.length === 0 ? (
        <div style={{ fontSize: 12, color: "var(--text-muted)", fontStyle: "italic" }}>
          Keine HERO-Dokument-Typen geladen — HERO-Token im Tenant pruefen.
        </div>
      ) : steps.length === 0 ? (
        <div style={{ fontSize: 12, color: "var(--text-muted)", fontStyle: "italic" }}>
          Keine Pipeline-Steps fuer diesen Projekttyp geladen.
        </div>
      ) : (
        <>
          {current.rules.length === 0 && (
            <div style={{ fontSize: 12, color: "var(--text-muted)", fontStyle: "italic", marginBottom: 10 }}>
              Keine Regeln. „+ Regel hinzufuegen" um die erste anzulegen.
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {current.rules.map((r, i) => (
              <div key={i} style={{
                display: "grid",
                gridTemplateColumns: "1fr auto 1fr auto",
                gap: 8, alignItems: "center",
                padding: 10, background: "#FAFAF8",
                border: "0.5px solid rgba(44,44,42,0.1)", borderRadius: 6,
              }}>
                <select
                  value={r.document_type_id}
                  onChange={(e) => update(i, { document_type_id: parseInt(e.target.value) })}
                  style={{ fontSize: 13 }}
                >
                  {documentTypes.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
                <span style={{ fontSize: 13, color: "var(--text-muted)" }}>→</span>
                <select
                  value={r.target_step_id}
                  onChange={(e) => update(i, { target_step_id: parseInt(e.target.value) })}
                  style={{ fontSize: 13 }}
                >
                  {steps.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <button
                  type="button"
                  onClick={() => remove(i)}
                  style={{ background: "transparent", border: "0.5px solid #D85A30", color: "#D85A30", borderRadius: 6, padding: "4px 10px", fontSize: 12, cursor: "pointer" }}
                >×</button>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 12 }}>
            <button
              type="button"
              onClick={addRule}
              className="pp-btn pp-btn-sm"
              style={{ background: "transparent", border: "0.5px solid rgba(44,44,42,0.2)", padding: "4px 12px", fontSize: 12, borderRadius: 6, cursor: "pointer" }}
            >+ Regel hinzufuegen</button>
          </div>
        </>
      )}

      <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end" }}>
        <button
          type="button"
          disabled={saving}
          onClick={async () => { setSaving(true); try { await onSave(items) } finally { setSaving(false) } }}
          className="pp-btn pp-btn-primary pp-btn-sm"
          title={dirty ? "Speichern" : "Nochmal speichern (loest u.a. pre-existing_skip-Cleanup aus)"}
        >{saving ? "Speichert…" : "Speichern"}</button>
      </div>
    </div>
  )
}

function DocumentTaskMapCard({
  tenant, typeKey, documentTypes, allByType, onSave,
}: {
  tenant: string
  typeKey: string
  documentTypes: DocType[]
  allByType: Record<string, DocTaskMap>
  onSave: (v: Record<string, DocTaskMap>) => Promise<void>
}) {
  // #95: Unterhaltsreinigungen stehen dauerhaft auf „Laufender Vertrag" — ein
  // Status-Switch hat dort nichts zu schalten. Trotzdem soll nach jedem
  // Arbeitsbericht jemand die Rechnung schreiben. Deshalb eine eigene Regel:
  // Dokument → Aufgabe am Vorgang, statt eines Sonderfalls im Status-Switch.
  const empty: DocTaskMap = { rules: [] }
  const [items, setItems] = useState<Record<string, DocTaskMap>>(allByType || {})
  const [saving, setSaving] = useState(false)
  useEffect(() => { setItems(allByType || {}) }, [allByType])

  // #97: Empfaenger = Mitarbeiter mit HERO-USER-ID (Aufgaben brauchen die
  // User-, nicht die Partner-ID). Dieselbe Liste wie im Aufgaben-Dialog.
  const [empfaenger, setEmpfaenger] = useState<{ id: number; hero_user_id: number | null; name: string }[]>([])
  useEffect(() => {
    if (!tenant) return
    let aktiv = true
    fetch(`/api/v1/calendar/monteure?tenant=${encodeURIComponent(tenant)}`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((d) => { if (aktiv) setEmpfaenger((d?.items || []).filter((m: { hero_user_id: number | null }) => m.hero_user_id != null)) })
      .catch(() => { if (aktiv) setEmpfaenger([]) })
    return () => { aktiv = false }
  }, [tenant])

  const current = items[typeKey] || empty
  const setRules = (rules: DocTaskRule[]) => {
    setItems({ ...items, [typeKey]: { rules } })
  }
  const addRule = () => {
    const dt = documentTypes.find((d) => /bericht/i.test(d.name)) || documentTypes[0]
    if (!dt) return
    setRules([...current.rules, {
      document_type_id: dt.id, document_type_name: dt.name,
      task_title: DOC_TASK_STANDARD_TITEL, due_days: 7,
    }])
  }
  const remove = (i: number) => setRules(current.rules.filter((_, idx) => idx !== i))
  const update = (i: number, patch: Partial<DocTaskRule>) => {
    setRules(current.rules.map((r, idx) => {
      if (idx !== i) return r
      const next = { ...r, ...patch }
      if (patch.document_type_id != null) {
        const d = documentTypes.find((x) => x.id === patch.document_type_id)
        if (d) next.document_type_name = d.name
      }
      return next
    }))
  }

  return (
    <div className="pp-card" style={{ padding: 18 }}>
      <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>Aufgabe nach Dokument-Erstellung</div>
      <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 14, lineHeight: 1.5 }}>
        Wenn an einem Vorgang dieses Projekttyps ein Dokument des gewaehlten Typs
        erstellt wird (z.&nbsp;B. ein Arbeitsbericht bei einer Unterhaltsreinigung),
        legt das Portal in HERO eine Aufgabe am Vorgang an — jedes Dokument genau
        einmal, Entwuerfe zaehlen nicht. Platzhalter im Titel:
        {" "}<code>{"{kunde}"}</code> <code>{"{dokument}"}</code> <code>{"{datum}"}</code> <code>{"{vorgang}"}</code>.
      </div>

      {documentTypes.length === 0 ? (
        <div style={{ fontSize: 12, color: "var(--text-muted)", fontStyle: "italic" }}>
          Keine HERO-Dokument-Typen geladen — HERO-Token im Tenant pruefen.
        </div>
      ) : (
        <>
          {current.rules.length === 0 && (
            <div style={{ fontSize: 12, color: "var(--text-muted)", fontStyle: "italic", marginBottom: 10 }}>
              Keine Regeln. „+ Regel hinzufuegen" um die erste anzulegen.
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {current.rules.map((r, i) => (
              <div key={i} style={{
                display: "grid",
                gridTemplateColumns: "minmax(140px, 1fr) auto 2fr minmax(140px, 1fr) auto auto",
                gap: 8, alignItems: "center",
                padding: 10, background: "#FAFAF8",
                border: "0.5px solid rgba(44,44,42,0.1)", borderRadius: 6,
              }}>
                <select
                  value={r.document_type_id}
                  onChange={(e) => update(i, { document_type_id: parseInt(e.target.value) })}
                  style={{ fontSize: 13 }}
                >
                  {documentTypes.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
                <span style={{ fontSize: 13, color: "var(--text-muted)" }}>→ Aufgabe</span>
                <input
                  type="text"
                  value={r.task_title}
                  placeholder={DOC_TASK_STANDARD_TITEL}
                  onChange={(e) => update(i, { task_title: e.target.value })}
                  style={{ fontSize: 13, width: "100%" }}
                />
                <select
                  aria-label="Zustaendig"
                  value={r.target_user_id ?? ""}
                  onChange={(e) => {
                    const uid = e.target.value === "" ? null : parseInt(e.target.value)
                    const name = uid == null ? "" : (empfaenger.find((m) => m.hero_user_id === uid)?.name || "")
                    update(i, { target_user_id: uid, target_user_name: name })
                  }}
                  style={{ fontSize: 13 }}
                >
                  <option value="">ohne Zustaendigen</option>
                  {/* gespeicherter Empfaenger, auch wenn die Liste (noch) nicht geladen ist */}
                  {r.target_user_id != null && !empfaenger.some((m) => m.hero_user_id === r.target_user_id) && (
                    <option value={r.target_user_id}>{r.target_user_name || "gespeicherter Mitarbeiter"}</option>
                  )}
                  {empfaenger.map((m) => <option key={m.id} value={m.hero_user_id!}>{m.name}</option>)}
                </select>
                <label style={{ fontSize: 12, color: "var(--text-muted)", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 4 }}>
                  faellig in
                  <input
                    type="number"
                    min={0}
                    max={365}
                    value={r.due_days ?? ""}
                    onChange={(e) => update(i, { due_days: e.target.value === "" ? null : parseInt(e.target.value) })}
                    style={{ fontSize: 13, width: 56 }}
                  />
                  Tagen
                </label>
                <button
                  type="button"
                  onClick={() => remove(i)}
                  style={{ background: "transparent", border: "0.5px solid #D85A30", color: "#D85A30", borderRadius: 6, padding: "4px 10px", fontSize: 12, cursor: "pointer" }}
                >×</button>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 12 }}>
            <button
              type="button"
              onClick={addRule}
              className="pp-btn pp-btn-sm"
              style={{ background: "transparent", border: "0.5px solid rgba(44,44,42,0.2)", padding: "4px 12px", fontSize: 12, borderRadius: 6, cursor: "pointer" }}
            >+ Regel hinzufuegen</button>
          </div>
        </>
      )}

      <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end" }}>
        <button
          type="button"
          disabled={saving}
          onClick={async () => { setSaving(true); try { await onSave(items) } finally { setSaving(false) } }}
          className="pp-btn pp-btn-primary pp-btn-sm"
        >{saving ? "Speichert…" : "Speichern"}</button>
      </div>
    </div>
  )
}

function AppointmentStepMapCard({
  typeKey, steps, calendarCategories, allByType, onSave,
}: {
  typeKey: string
  steps: PipelineStep[]
  calendarCategories: CalendarCategory[]
  allByType: Record<string, ApptStepMap>
  onSave: (v: Record<string, ApptStepMap>) => Promise<void>
}) {
  const empty: ApptStepMap = { rules: [] }
  const [items, setItems] = useState<Record<string, ApptStepMap>>(allByType || {})
  const [saving, setSaving] = useState(false)
  useEffect(() => { setItems(allByType || {}) }, [allByType])
  const dirty = JSON.stringify(items[typeKey] || empty) !== JSON.stringify(allByType[typeKey] || empty)

  const current = items[typeKey] || empty
  const setRules = (rules: ApptStepRule[]) => {
    setItems({ ...items, [typeKey]: { rules } })
  }
  const addRule = () => {
    const cat = calendarCategories[0]
    const st = steps[0]
    if (!cat || !st) return
    setRules([...current.rules, {
      calendar_category_id: cat.id, calendar_category_name: cat.name,
      target_step_id: st.id, target_step_name: st.name,
    }])
  }
  const remove = (i: number) => setRules(current.rules.filter((_, idx) => idx !== i))
  const update = (i: number, patch: Partial<ApptStepRule>) => {
    setRules(current.rules.map((r, idx) => {
      if (idx !== i) return r
      const next = { ...r, ...patch }
      if (patch.calendar_category_id != null) {
        const c = calendarCategories.find((x) => x.id === patch.calendar_category_id)
        if (c) next.calendar_category_name = c.name
      }
      if (patch.target_step_id != null) {
        const s = steps.find((x) => x.id === patch.target_step_id)
        if (s) next.target_step_name = s.name
      }
      return next
    }))
  }

  return (
    <div className="pp-card" style={{ padding: 18 }}>
      <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>Status nach Termin-Durchführung</div>
      <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 14, lineHeight: 1.5 }}>
        Wenn ein Termin der gewählten Kalender-Kategorie für ein Projekt dieses
        Typs vergangen ist (end_time {"<"} jetzt), wird der Status auf den
        ausgewählten Step gesetzt. Wird alle 15 Min gepollt
        (Idempotenz: jeder Termin schaltet genau einmal).
      </div>

      {calendarCategories.length === 0 ? (
        <div style={{ fontSize: 12, color: "var(--text-muted)", fontStyle: "italic" }}>
          Keine Kalender-Kategorien geladen — HERO-Token im Tenant prüfen.
        </div>
      ) : steps.length === 0 ? (
        <div style={{ fontSize: 12, color: "var(--text-muted)", fontStyle: "italic" }}>
          Keine Pipeline-Steps für diesen Projekttyp geladen.
        </div>
      ) : (
        <>
          {current.rules.length === 0 && (
            <div style={{ fontSize: 12, color: "var(--text-muted)", fontStyle: "italic", marginBottom: 10 }}>
              Keine Regeln. „+ Regel hinzufügen" um die erste anzulegen.
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {current.rules.map((r, i) => (
              <div key={i} style={{
                display: "grid",
                gridTemplateColumns: "1fr auto 1fr auto",
                gap: 8, alignItems: "center",
                padding: 10, background: "#FAFAF8",
                border: "0.5px solid rgba(44,44,42,0.1)", borderRadius: 6,
              }}>
                <select
                  value={r.calendar_category_id}
                  onChange={(e) => update(i, { calendar_category_id: parseInt(e.target.value) })}
                  style={{ fontSize: 13 }}
                >
                  {calendarCategories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <span style={{ fontSize: 13, color: "var(--text-muted)" }}>→</span>
                <select
                  value={r.target_step_id}
                  onChange={(e) => update(i, { target_step_id: parseInt(e.target.value) })}
                  style={{ fontSize: 13 }}
                >
                  {steps.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <button
                  type="button"
                  onClick={() => remove(i)}
                  style={{ background: "transparent", border: "0.5px solid #D85A30", color: "#D85A30", borderRadius: 6, padding: "4px 10px", fontSize: 12, cursor: "pointer" }}
                >×</button>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 12 }}>
            <button
              type="button"
              onClick={addRule}
              className="pp-btn pp-btn-sm"
              style={{ background: "transparent", border: "0.5px solid rgba(44,44,42,0.2)", padding: "4px 12px", fontSize: 12, borderRadius: 6, cursor: "pointer" }}
            >+ Regel hinzufügen</button>
          </div>
        </>
      )}

      <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end" }}>
        <button
          type="button"
          disabled={saving}
          onClick={async () => { setSaving(true); try { await onSave(items) } finally { setSaving(false) } }}
          className="pp-btn pp-btn-primary pp-btn-sm"
          title={dirty ? "Speichern" : "Nochmal speichern (loest u.a. pre-existing_skip-Cleanup aus)"}
        >{saving ? "Speichert…" : "Speichern"}</button>
      </div>
    </div>
  )
}

function TransitionRulesCard({
  typeKey, steps, allByType, onSave,
}: {
  typeKey: string
  steps: { id: number; name: string }[]
  allByType: Record<string, PipelineTransitionRule[]>
  onSave: (rules_by_type: Record<string, PipelineTransitionRule[]>) => Promise<void>
}) {
  const [items, setItems] = useState<Record<string, PipelineTransitionRule[]>>(allByType || {})
  const [saving, setSaving] = useState(false)
  useEffect(() => { setItems(allByType || {}) }, [allByType])

  const list = items[typeKey] || []
  const dirty = JSON.stringify(items[typeKey] || []) !== JSON.stringify(allByType[typeKey] || [])

  const setList = (next: PipelineTransitionRule[]) => setItems({ ...items, [typeKey]: next })

  const addRule = () => {
    const firstStep = steps[0]
    if (!firstStep) return
    setList([...list, {
      trigger_pattern: "", from_step_id: null,
      to_step_id: firstStep.id, to_step_name: firstStep.name, enabled: true,
    }])
  }
  const remove = (i: number) => setList(list.filter((_, idx) => idx !== i))
  const update = (i: number, patch: Partial<PipelineTransitionRule>) => {
    setList(list.map((r, idx) => {
      if (idx !== i) return r
      const next = { ...r, ...patch }
      if (patch.to_step_id) {
        const s = steps.find((x) => x.id === patch.to_step_id)
        if (s) next.to_step_name = s.name
      }
      return next
    }))
  }

  return (
    <div className="pp-card" style={{ padding: 18 }}>
      <div style={{ fontSize: 14, fontWeight: 500 }}>Status-Uebergangs-Regeln</div>
      <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4, marginBottom: 12 }}>
        Mail-Trigger → Pipeline-Step. Claude bekommt diese Regeln im Kontext
        und setzt den Step entsprechend wenn eine Mail die Stichworte enthaelt.
      </div>

      {list.length === 0 && (
        <div style={{ fontSize: 12, color: "var(--text-muted)", fontStyle: "italic", marginBottom: 10 }}>
          Keine Uebergangs-Regeln fuer diesen Projekttyp. Claude entscheidet rein per Heuristik.
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {list.map((r, i) => (
          <div key={i} style={{
            border: "0.5px solid rgba(44,44,42,0.15)", borderRadius: 8, padding: 12,
            display: "grid", gridTemplateColumns: "minmax(0, 1.5fr) minmax(0, 1fr) minmax(0, 1fr) minmax(56px, 80px) auto", gap: 8, alignItems: "flex-end",
          }}>
            <div>
              <label className="pp-form-label" style={{ fontSize: 12 }}>Stichworte (Komma)</label>
              <input
                value={r.trigger_pattern}
                onChange={(e) => update(i, { trigger_pattern: e.target.value })}
                placeholder="bestätige, auftrag bestätigt, ja machen wir"
                style={{ fontSize: 13 }}
              />
            </div>
            <div>
              <label className="pp-form-label" style={{ fontSize: 12 }}>Aus Step (optional)</label>
              <select
                value={r.from_step_id ?? ""}
                onChange={(e) => update(i, { from_step_id: e.target.value ? parseInt(e.target.value) : null })}
              >
                <option value="">— jeder Step —</option>
                {steps.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className="pp-form-label" style={{ fontSize: 12 }}>Wechsel zu</label>
              <select
                value={r.to_step_id}
                onChange={(e) => update(i, { to_step_id: parseInt(e.target.value) })}
              >
                {steps.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <label style={{ fontSize: 12, display: "inline-flex", alignItems: "center", gap: 4 }}>
              <input type="checkbox" checked={r.enabled} onChange={(e) => update(i, { enabled: e.target.checked })} />
              aktiv
            </label>
            <button
              type="button"
              onClick={() => remove(i)}
              style={{ background: "transparent", border: "0.5px solid #D85A30", color: "#D85A30", borderRadius: 6, padding: "6px 10px", fontSize: 12, cursor: "pointer", height: 32 }}
            >Loeschen</button>
          </div>
        ))}
      </div>

      <div style={{ marginTop: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <button type="button" onClick={addRule} className="pp-btn pp-btn-sm">+ Regel hinzufuegen</button>
        <button
          type="button"
          disabled={!dirty || saving}
          onClick={async () => { setSaving(true); try { await onSave(items) } finally { setSaving(false) } }}
          className="pp-btn pp-btn-primary pp-btn-sm"
        >{saving ? "Speichert…" : "Speichern"}</button>
      </div>
    </div>
  )
}
